/**
 * @copyright 2026 Fortee Uniforme Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying prohibited.
 * Protected under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 */

// fortee-logo-placement.js - Phase 37 shared geometry for logo size & position.
//
// Every function here is PURE: explicit arguments in, plain object out. No
// Alpine, no DOM, no globals beyond this one namespace. That boundary is
// deliberate - 37-RESEARCH.md Pitfall 7 records that extracting a *stateful*
// Alpine slice by line range broke four behaviours silently during the logo
// editor extraction. Only pure functions with explicit arguments move.
//
// It is also the only mechanism by which this phase's arithmetic is testable:
// the repo has no test framework and is not getting one here. Run the
// assertions with:  node scripts/test-logo-placement.cjs   (npm run test:geometry)
//
// THREE LOAD-BEARING DECISIONS, settled here so both builders inherit them:
//
// 1. PHYSICAL SIZE COMES FROM THE ZONE MAPPER OR IT DOES NOT EXIST.
//    The only source is the zone's own wIn/hIn, typed per product in the zone
//    mapper - a real measurement of THIS garment in THIS photo. The print_areas
//    metaobject's maxWidth is a catalogue figure SHARED by up to seventy
//    products; it can no longer size anything (Ugo, 2026-08-02). It is still
//    read, still reported as areaWIn/declaredHIn, and still raises
//    widthMismatch / heightMismatch, but only ever as a cross-check.
//
// 2. A ZONE WITH NO TYPED WIDTH IS UNMEASURED, NOT UNUSABLE.
//    zoneGeometry used to return null there, which switched both builders to a
//    legacy percentage overlay that emits pointer-events:none - so 676 of the
//    live store's 680 zones could not be dragged or resized at all. It now
//    resolves in ZONE-WIDTH SPACE instead: the drawn box is its own unit, so
//    zoneWIn is exactly 1, pxPerIn is the box's rendered width, and the result
//    carries `measured: false`. Every clamp, drag, resize and fit path is
//    scale-free arithmetic and works unchanged in that space.
//
//    THE UNIT IS THE ZONE, NOT THE PIXEL, and that is load-bearing. One
//    placement record is rendered on surfaces of different sizes - the desktop
//    hero and the mobile corner card measure the same zone at different pixel
//    widths, and the 2-step bakes against a nominal 600x600 that matches
//    neither. A record in raw pixels would mean something different on each,
//    and a logo sized on one surface would be wrong on the others. In zone
//    widths the record is scale-free, exactly as an inch record is, and pxPerIn
//    absorbs the difference per surface - the same mechanism, the same shape.
//
//    THE FLAG IS A SAFETY BOUNDARY, NOT A HINT. When it is false the numbers in
//    this object are FRACTIONS OF THE DRAWN BOX. Nothing may print them with an
//    inch or centimetre label, and nothing may put them on a cart property, a
//    filename or any server payload that claims a physical dimension. Callers
//    gate on `geo.measured === true`, never on the absence of false.
//
//    Every tunable that is a PHYSICAL constant (a 0.75 in minimum logo, a
//    0.25 in snap tolerance, a 0.25 in arrow-key step) is re-expressed in that
//    space by inSpace() below, because a physical constant compared against a
//    fraction is a units bug wearing a plausible number.
//
// 3. THE DEFAULT MUST REPRODUCE TODAY'S OUTPUT. standardPlacement re-derives
//    the existing compositor's contain-fit-and-anchor result
//    (builder-shared-services.js:486-494) rather than using the design
//    prototype's constants. If "Standard size" produced anything else, every
//    existing customer's default placement would silently shift and every
//    cached mockup would disagree with the new render.

(function () {
  'use strict';

  var CM_PER_IN = 2.54;

  // Every one of these is a tunable. The SPEC marks the snap tolerance tunable
  // explicitly; CONTEXT requires the minimum width to be a defensible per-area
  // floor rather than a global constant. Functions that consume a tolerance
  // take it as an optional argument defaulting to the entry here, so a caller
  // can override without editing this file.
  var DEFAULTS = {
    snapTolIn: 0.25,
    stdTolIn: 0.06,
    keyStepIn: 0.25,
    keyStepShiftIn: 1,
    minFloorIn: 0.75,
    minZoneFrac: 0.15,
    heightMismatchWarnIn: 0.5,
    stdFitFrac: 0.65
  };

  // The print area every physical tunable above was chosen against: the 12 in
  // full front, which is also the fixture the assertions at the bottom of this
  // file are built on. It is the ONLY number here that is not itself a tunable,
  // and it exists so the conversion below has a stated basis instead of three
  // hand-picked fractions that would drift from the constants they mirror.
  var REFERENCE_ZONE_IN = 12;

  // Re-express a PHYSICAL tunable in whatever space a geometry lives in.
  //
  // An unmeasured zone has no inches, so "0.25 in of snap" and "0.75 in of
  // minimum logo" are not merely unavailable there — passing them through
  // unconverted is a units bug that produces a plausible-looking number and a
  // control that silently stops working. In zone-width space 0.25 would be a
  // quarter of the whole print area of snap magnetism, and 0.75 would forbid any
  // logo under three quarters of the zone.
  //
  // The conversion keeps the PROPORTION rather than the magnitude: whatever
  // fraction of the reference zone the tunable represents, it represents the
  // same fraction of this one. So a customer moving between a measured and an
  // unmeasured product feels the same control, and no call site has to know
  // which kind it is on.
  function inSpace(geo, inches) {
    if (geo && geo.measured === false) {
      return num(geo.zoneWIn) * (num(inches) / REFERENCE_ZONE_IN);
    }
    return num(inches);
  }

  function num(v) {
    var n = Number(v);
    return isFinite(n) ? n : 0;
  }

  function clamp(v, lo, hi) {
    if (!(hi > lo)) return lo;
    return v < lo ? lo : v > hi ? hi : v;
  }

  function round4(v) {
    return Math.round(num(v) * 10000) / 10000;
  }

  // ---------------------------------------------------------------------------
  // Units
  // ---------------------------------------------------------------------------

  // Normalise a merchant-authored dimension to inches. Matches on prefix so both
  // singular and plural metaobject tokens work.
  //
  // An empty, missing or unrecognised unit falls back to dividing by CM_PER_IN
  // because the live store authors these in centimetres - see
  // scripts/01-create-print-area-entries.graphql, where every seeded entry is
  // {"value": N, "unit": "CENTIMETERS"}. Treating a bare number as inches would
  // be a silent 2.54x error on real data.
  function toInches(magnitude, unit) {
    var n = Number(magnitude);
    if (!isFinite(n) || n < 0) return 0;
    var u = String(unit || '').toUpperCase().replace(/[^A-Z]/g, '');
    if (u.indexOf('CENTIMET') === 0 || u === 'CM') return n / CM_PER_IN;
    if (u.indexOf('MILLIMET') === 0 || u === 'MM') return n / (CM_PER_IN * 10);
    if (u.indexOf('INCH') === 0 || u === 'IN') return n;
    if (u.indexOf('METER') === 0 || u.indexOf('METRE') === 0 || u === 'M') {
      return (n * 100) / CM_PER_IN;
    }
    return n / CM_PER_IN;
  }

  // Display-side inverse, used by the unit toggle. Only inch and centimetre are
  // needed; anything else is returned unchanged.
  function fromInches(inches, unit) {
    var n = Number(inches);
    if (!isFinite(n)) return 0;
    var u = String(unit || '').toLowerCase();
    if (u === 'cm') return n * CM_PER_IN;
    return n;
  }

  // ---------------------------------------------------------------------------
  // Zone geometry
  // ---------------------------------------------------------------------------

  // Rebuild the zone rect in rendered CSS pixels from a logo_zones entry.
  //
  // This mirrors compositeMockup's arithmetic (builder-shared-services.js:467-484)
  // line for line. Note the deliberate asymmetry: BOTH the zone width and the
  // zone height are fractions of the box WIDTH, while "top" is a fraction of the
  // box HEIGHT. That is not a bug - the mapper authors w/h as percentages of
  // image width so a zone keeps its shape across differently-tall product
  // photos. Two existing consumers depend on it:
  //   builder-shared-services.js:472-479  (the compositor)
  //   single-builder.js:467-479           (_scaleZoneDims, the live overlay)
  // Do not "fix" it here without changing all three together.
  function zonePx(rawZone, boxW, boxH) {
    var z = rawZone || {};
    var W = num(boxW);
    var H = num(boxH);

    var wSrc = z.w != null ? z.w : (z.width != null ? z.width : (z.size != null ? z.size : '10%'));
    var hSrc = z.h != null ? z.h : (z.height != null ? z.height : wSrc);
    var wVal = String(wSrc);
    var hVal = String(hSrc);

    var zW, zH;
    if (wVal.indexOf('%') !== -1) {
      zW = W * (parseFloat(wVal) / 100);
      zH = W * (parseFloat(hVal) / 100);
    } else {
      zW = Number(wVal) * (W / 600);
      zH = Number(hVal) * (W / 600);
    }
    if (!isFinite(zW)) zW = 0;
    if (!isFinite(zH)) zH = 0;

    var centerX = W * ((parseFloat(z.left) || 50) / 100);
    var topEdgeY = H * ((parseFloat(z.top) || 25) / 100);

    var angle = Number(z.angle);
    if (!isFinite(angle)) angle = 0;

    return { x: centerX - zW / 2, y: topEdgeY, w: zW, h: zH, angle: angle };
  }

  // The content rect of an image rendered with contain fit inside a box.
  //
  // The garment element's box is NOT the painted garment whenever a max-height
  // constraint bites (37-RESEARCH.md Pitfall 3 - .ftb-hero-img carries
  // object-fit:contain plus max-height:364px). Zone percentages must resolve
  // against the painted content, not the element box, or the overlay drifts.
  function paintedRect(natW, natH, boxW, boxH) {
    var nw = Number(natW), nh = Number(natH), bw = Number(boxW), bh = Number(boxH);
    if (!(isFinite(nw) && nw > 0) || !(isFinite(nh) && nh > 0) ||
        !(isFinite(bw) && bw > 0) || !(isFinite(bh) && bh > 0)) {
      return { x: 0, y: 0, w: num(boxW), h: num(boxH) };
    }
    var s = Math.min(bw / nw, bh / nh);
    var w = nw * s;
    var h = nh * s;
    return { x: (bw - w) / 2, y: (bh - h) / 2, w: w, h: h };
  }

  // The composed entry point. Returns null only when there is no usable RECTANGLE
  // at all - no drawn box, or one with no width. A missing physical measurement
  // is not that case: it resolves in zone-width space with measured:false
  // (decision 2 in the header). A merchant-setup gap must surface as an absent NUMBER, never
  // as a fabricated one - and never as an absent CONTROL, which is what it did
  // between 2026-08-02 morning and this change.
  function zoneGeometry(area, rawZone, boxW, boxH) {
    var a = area || {};
    var z = rawZone || {};

    var areaWIn = toInches(a.maxWidth, a.maxWidthUnit);

    // THE ZONE'S OWN REAL-WORLD WIDTH WINS, when it has one.
    //
    // Without it the scale is inferred: pxPerIn = drawnBoxWidthPx / areaWIn,
    // which ASSUMES the hand-drawn box is exactly the print area's catalogue
    // width. One print_areas metaobject is shared by every product, while each
    // product has its own photo and its own physical garment — a youth tee and
    // a 3XL hoodie cannot both be right, and nothing detects a mis-drawn box.
    // Every on-screen inch and every inch written to the cart inherits that
    // error. See 37-STATUS-2026-07-28.md issue 1.
    //
    // zone.wIn is typed per product in the zone mapper, so it is a real
    // measurement rather than an assumption.
    //
    // THE CATALOGUE FALLBACK IS GONE (Ugo, 2026-08-02). It used to read
    // `hasZoneW ? zoneDeclWIn : areaWIn`, which meant a product with no typed
    // measurement silently inherited the shared metaobject's width. That is the
    // whole defect: one `print_areas` entry is referenced by up to 70 products,
    // so "the left sleeve is 8cm" was a single number standing in for every
    // garment that happens to use the name, and editing it moved all of them.
    //
    // Now the zone mapper is the ONLY source of physical size. A zone with no
    // typed width has no measurement, and nothing here may invent one.
    // areaWIn is still computed and still reported below, but only for
    // comparison and mismatch warnings; it can no longer size anything.
    var zoneDeclWIn = Number(z.wIn);
    var hasZoneW = isFinite(zoneDeclWIn) && zoneDeclWIn > 0;

    var px = zonePx(rawZone, boxW, boxH);
    if (!(isFinite(px.w) && px.w > 0)) return null;

    // WHAT "NO MEASUREMENT" MEANS, AND WHAT IT MUST NOT COST.
    //
    // Returning null here — which is what this did for one morning — is the
    // honest answer to "how many inches wide is this zone?". It was also the
    // wrong answer to the question the callers actually ask, which is "can this
    // logo be moved?". Both builders read null as "no geometry", fell back to a
    // legacy percentage overlay that carries pointer-events:none, and the logo
    // became undraggable, unresizable and handle-less. On the live store that is
    // 676 of 680 zones across 90 of 92 configured products: effectively the
    // whole catalogue lost its editor to protect a label.
    //
    // The two are separable, because every other function in this file is
    // SCALE-FREE. clampPlace, clampWidth, standardPlacement, clampRectPx and
    // logoRectPx only ever compare lengths against other lengths from the same
    // geometry; not one of them contains an inch. So an unmeasured zone gets a
    // unit of its own and every one of those paths runs unchanged. The
    // alternative, a parallel pixel-only drag path, would be a second copy of
    // the clamp arithmetic, and the two would drift the first time either was
    // fixed.
    //
    // THE UNIT IS THE ZONE'S OWN WIDTH — zoneWIn is exactly 1 — and NOT the
    // rendered pixel, which is the obvious choice and the wrong one. A record is
    // written once and rendered on surfaces that measure the same zone at
    // different sizes: the desktop hero, the mobile corner card
    // (single-builder.js _stickyGeoContext, "same inch values, different
    // pixels-per-inch"), and the 2-step's nominal 600x600 bake probe. In pixels
    // a width of 80 would mean a different fraction of the zone on each of them,
    // so a logo sized on the hero would come out wrong on the card and wrong
    // again in the bake. In zone widths the record is scale-free, exactly as an
    // inch record is, and pxPerIn absorbs the per-surface difference — the same
    // mechanism the measured path already uses, which is the point: there is one
    // mechanism, not two.
    //
    // What is NOT separable is the label. `measured` is the boundary: false
    // means the numbers below are FRACTIONS OF THE DRAWN BOX, and a caller that
    // prints them with a unit, or sends them anywhere that means a physical
    // size, is fabricating a dimension. Callers test `measured === true`, so a
    // record that lost the flag fails closed.
    var measured = hasZoneW;
    var wIn = measured ? zoneDeclWIn : 1;

    var pxPerIn = px.w / wIn;
    if (!(isFinite(pxPerIn) && pxPerIn > 0)) return null;

    // Height. Three sources, in falling order of authority.
    //
    // The rule changed on 2026-07-30. It used to be "the zone's own measurement
    // first, then the DRAWN box" — the mapper box sits on this product's real
    // photo, so it knows where the region is, while max_height is a catalogue
    // figure that does not.
    //
    // That reasoning holds for POSITION and it is still what px.x/px.y provide.
    // It does not hold for SIZE, and mixing the two produced a boundary that was
    // wrong on one axis. When a zone declares wIn but not hIn, pxPerIn is derived
    // from the TYPED width while the height came from the DRAWN box:
    //
    //     drawnHIn = px.h / pxPerIn = z.wIn * (px.h / px.w)
    //
    // i.e. the typed width scaled by the hand-drawn rectangle's aspect. Draw a
    // non-square box over a square print area and the boundary reports 3.5 x 3.0
    // for a 10 x 10 cm area — Ugo's screenshot, 2026-07-30: the label and the box
    // agreed with each other and both disagreed with the actual print area.
    //
    // So: an explicit per-zone hIn wins. Failing that, the DRAWN box.
    //
    // THE 'area-aspect' BRANCH IS GONE (Ugo, 2026-08-02), for the same reason
    // the width fallback is. It scaled the shared metaobject's aspect onto this
    // zone, so a hand-drawn SQUARE over a catalogue entry declaring 8cm x 25cm
    // rendered at 1:3.125 — the exact symptom reported for S05610's left sleeve.
    // The drawn box is this product's own photo and this product's own zone; the
    // catalogue aspect belongs to whichever garment happened to be measured first.
    //
    // Both remaining sources are the mapper's: an explicitly typed hIn, or the
    // shape of the box drawn on this product's image. Nothing shared can reach
    // the height any more. declaredHIn is still reported and heightMismatch
    // still flags a disagreement, so a mis-drawn zone stays visible.
    //
    // AN UNMEASURED ZONE TAKES THE DRAWN HEIGHT AND ONLY THE DRAWN HEIGHT.
    // A typed hIn with no typed wIn is half a measurement: it would put an inch
    // figure on one axis of a box whose other axis is pixels, and every ratio
    // taken across the two — the aspect ceiling in clampPlace and clampWidth
    // above all — would be wrong by whatever pxPerIn happens to be. Half a
    // measurement is worse than none, because it looks like one.
    var declaredHIn = toInches(a.maxHeight, a.maxHeightUnit);
    var drawnHIn = px.h / pxPerIn;
    var zoneDeclHIn = Number(z.hIn);
    var hasZoneH = measured && isFinite(zoneDeclHIn) && zoneDeclHIn > 0;
    var zoneHIn = hasZoneH ? zoneDeclHIn : drawnHIn;
    // Where the height came from, so a caller can tell a measured boundary from
    // an inferred one without re-deriving the precedence.
    var heightSource = hasZoneH ? 'zone' : 'drawn';

    // A merchant who types one width in the mapper and a different one in admin
    // must be told which one is being used, not silently overridden.
    var widthDisagrees = hasZoneW && areaWIn > 0 &&
      Math.abs(zoneDeclWIn - areaWIn) > DEFAULTS.heightMismatchWarnIn;

    // What height the catalogue's own aspect would imply at this zone's real
    // width. Purely advisory since 2026-08-02 — it is the number the removed
    // 'area-aspect' branch used to return, retained ONLY to flag a disagreement
    // so a mis-drawn zone or a stale metaobject stays visible.
    //
    // BOTH CROSS-CHECKS ARE OFF WHEN THE ZONE IS UNMEASURED, and that is not a
    // gap being tolerated — it is the only defensible answer. A mismatch flag
    // asserts that two figures describing the same thing disagree. With no typed
    // width there is no second figure: zoneWIn is a pixel count and areaWIn is a
    // catalogue inch, and subtracting them would compare a screen to a garment
    // and fire on essentially every zone in the store. The merchant is told what
    // is actually true — "this zone has no measurement" — by `measured` itself,
    // which the builders warn on.
    var areaImpliedHIn = (measured && declaredHIn > 0 && areaWIn > 0)
      ? wIn * (declaredHIn / areaWIn)
      : null;
    var areaDisagrees = areaImpliedHIn !== null &&
      Math.abs(zoneHIn - areaImpliedHIn) > DEFAULTS.heightMismatchWarnIn;

    return {
      px: px,
      pxPerIn: pxPerIn,
      // INCHES WHEN TRUE, FRACTIONS OF THE DRAWN BOX WHEN FALSE. The two are
      // indistinguishable by inspection — 1 is a plausible value in either space
      // — which is exactly why the flag is not optional and why every consumer
      // tests it explicitly rather than sniffing the magnitude.
      measured: measured,
      zoneWIn: wIn,
      zoneHIn: zoneHIn,
      declaredHIn: declaredHIn,
      drawnHIn: drawnHIn,
      areaWIn: areaWIn,
      boxHpx: zoneHIn * pxPerIn,
      heightSource: heightSource,
      angle: px.angle,
      // 'zone'  = calibrated against a real measurement typed for THIS product.
      // 'drawn' = no measurement exists; the scale is the drawn box itself and
      //           every length here is a fraction of that box.
      // 'area' is GONE. It named the shared-metaobject fallback, which was
      // removed on 2026-08-02 — leaving the string in place would have let a
      // consumer keep testing for a branch that can no longer be taken.
      scaleSource: measured ? 'zone' : 'drawn',
      widthMismatch: widthDisagrees,
      // Compare the DRAWN box against the height actually in use.
      //
      // This used to compare declaredHIn against zoneHIn, which was meaningful
      // only while the drawn box won: it asked "does the catalogue disagree with
      // what we are using?". Now that the declared aspect usually IS what we are
      // using, that comparison is trivially zero and would flag nothing. The
      // question worth asking is unchanged in spirit — "does the hand-drawn box
      // disagree with the real print area?" — so it is now asked of the drawn
      // height, which keeps a mis-drawn zone visible instead of silently
      // reconciled.
      // The catalogue no longer SIZES anything, but it is still the best
      // available cross-check on a hand-drawn zone, so it still WARNS.
      //
      // The old first term compared the drawn height against the resolved one.
      // Once the area-aspect branch was removed those are identical whenever the
      // height comes from the drawn box, so that term silently became dead for
      // the common case and this flag would have gone quiet exactly when a
      // mis-drawn zone most needs surfacing. It is kept — it still catches a
      // typed hIn that disagrees with the drawn shape — and joined by an
      // explicit comparison against the aspect the catalogue implies.
      heightMismatch:
        Math.abs(drawnHIn - zoneHIn) > DEFAULTS.heightMismatchWarnIn ||
        areaDisagrees ||
        widthDisagrees
    };
  }

  // The per-area minimum width floor.
  //
  // A global two-inch minimum is wrong: a left-chest zone is only about four
  // inches wide, so a 2in floor would forbid half its range. The fraction is a
  // proposal pending confirmation - tracked as Assumption A5 in 37-RESEARCH.md.
  function minWidthIn(geo) {
    if (!geo) return DEFAULTS.minFloorIn;
    var byFrac = num(geo.zoneWIn) * DEFAULTS.minZoneFrac;
    // minFloorIn is a PHYSICAL floor — three quarters of an inch is about the
    // smallest logo worth printing, a statement about ink on fabric rather than
    // about the control — so it goes through inSpace. On an unmeasured zone that
    // makes it 0.0625 of the zone width, which the 15% proportional floor beats
    // outright; the effect is that an unmeasured zone is governed entirely by
    // the proportion, which is the only rule that means anything without a real
    // measurement. Left as a max rather than branched, so there is one
    // expression to read and no second path to keep in step.
    var m = Math.max(inSpace(geo, DEFAULTS.minFloorIn), byFrac);
    return Math.min(m, num(geo.zoneWIn));
  }

  // The three gesture tunables that are PHYSICAL, resolved for whichever space
  // the geometry lives in. Each is a one-line application of inSpace, and each
  // exists so a builder never has to know which space it is on — it asks for a
  // tolerance, a step or a tolerance band and gets one that means something.

  // Centre-snap tolerance. An explicit opts.snapTolIn is a PHYSICAL distance —
  // the builders expose it as a tunable expressed in inches — so it is converted
  // rather than ignored: a caller that widened the magnetism to half an inch
  // still gets proportionally wider magnetism on an unmeasured zone.
  function snapTolFor(geo, opts) {
    var o = opts || {};
    return inSpace(geo, typeof o.snapTolIn === 'number' ? o.snapTolIn : DEFAULTS.snapTolIn);
  }

  // Arrow-key increment for the width slider. Unconverted, 0.25 would be a
  // quarter of the entire print area per press — a control that overshoots on
  // the first keystroke rather than one that is unavailable.
  function keyStepFor(geo, shift) {
    return inSpace(geo, shift ? DEFAULTS.keyStepShiftIn : DEFAULTS.keyStepIn);
  }

  // How close to the default counts AS the default, for the restore button's
  // completion mark. Unconverted, 0.06 would be six percent of the zone, so a
  // clearly visible nudge would keep the mark lit and the button would claim
  // nothing had changed.
  function stdTolFor(geo) {
    return inSpace(geo, DEFAULTS.stdTolIn);
  }

  // ---------------------------------------------------------------------------
  // Slider range arithmetic
  // ---------------------------------------------------------------------------

  // The slider's two conversions live HERE rather than on the Alpine component
  // so they are assertable. A control that silently mis-maps its own range is
  // exactly the bug this module exists to catch: it looks correct at both ends
  // and is wrong everywhere in between, which no amount of clicking finds.
  //
  // The endpoints are always supplied by the caller from the ACTIVE print area
  // (minWidthIn(geo) and geo.zoneWIn). No literal width lives here, and none
  // may ever be added - the whole point of the pair is that the range is
  // per-area.
  function fractionToInches(frac, minIn, maxIn) {
    var lo = num(minIn);
    var hi = num(maxIn);
    if (!(hi > lo)) return lo;
    var f = Number(frac);
    if (!isFinite(f)) return lo;
    return lo + clamp(f, 0, 1) * (hi - lo);
  }

  function inchesToFraction(wIn, minIn, maxIn) {
    var lo = num(minIn);
    var hi = num(maxIn);
    if (!(hi > lo)) return 0;
    var v = Number(wIn);
    if (!isFinite(v)) return 0;
    return clamp((v - lo) / (hi - lo), 0, 1);
  }

  // ---------------------------------------------------------------------------
  // Placement
  // ---------------------------------------------------------------------------

  // Reproduce the compositor's contain-fit-and-anchor result exactly
  // (builder-shared-services.js:486-494), so a customer who never touches the
  // control gets a byte-identical mockup to the one they get today.
  //
  // vAlign / hAlign arrive already resolved by forteeShared.anchorFromRef and
  // forteeShared.hAnchorFromRef. This module does not re-implement anchor parsing.
  // opts (all optional, and all default to the historic behaviour so existing
  // callers are unaffected):
  //   fitFrac — override DEFAULTS.stdFitFrac. Pass 1 for a true contain fit,
  //             i.e. "as large as possible without exceeding the print area".
  //   centre  — ignore vAlign/hAlign and centre the logo on both axes.
  function standardPlacement(geo, artAspect, vAlign, hAlign, opts) {
    if (!geo) return { wIn: 0, lxIn: 0, lyIn: 0 };
    var o = opts || {};
    var aspect = Number(artAspect);
    if (!(isFinite(aspect) && aspect > 0)) aspect = 1;

    var zW = num(geo.px && geo.px.w);
    var zH = num(geo.px && geo.px.h);

    // Fit to a FRACTION of the zone, not the whole thing.
    //
    // A pure contain fit makes the logo touch the zone edge, which on a square
    // area with a square logo means the logo fills the print area exactly —
    // both boxes land on the same rectangle and there is nowhere to drag to.
    // The design prototype's own numbers show the intent: an 11 po logo in a
    // 12 po zone, ~92% of the width, with margin left over. DEFAULTS.stdFitFrac
    // is that fraction and is tunable.
    //
    // The single builder overrides this to 1 (Ugo's call 2026-07-29: the logo
    // must open as large as the print area allows). The tradeoff above is real
    // and accepted: when the artwork's aspect matches the zone's, the logo will
    // fill it and there is no slack to drag on either axis. Any other aspect
    // leaves slack on one axis, and the resize handle always recovers room.
    var frac = (typeof o.fitFrac === 'number' && isFinite(o.fitFrac) && o.fitFrac > 0)
      ? Math.min(o.fitFrac, 1)
      : DEFAULTS.stdFitFrac;
    var fitW = Math.min(zW, zH * aspect) * frac;
    var fitH = fitW / aspect;

    var leftoverW = zW - fitW;
    var leftoverH = zH - fitH;

    var dx = o.centre
      ? leftoverW / 2
      : (hAlign === 'left' ? 0 : hAlign === 'right' ? leftoverW : leftoverW / 2);
    var dy = o.centre
      ? leftoverH / 2
      : (vAlign === 'center' ? leftoverH / 2 : vAlign === 'bottom' ? leftoverH : 0);

    var ppi = num(geo.pxPerIn) || 1;
    return { wIn: fitW / ppi, lxIn: dx / ppi, lyIn: dy / ppi };
  }

  // Clamp the box inside the zone on all four sides with the aspect locked, and
  // pull to exact horizontal centre inside a tunable tolerance. Never mutates
  // its input.
  function clampPlace(lxIn, lyIn, wIn, artAspect, geo, opts) {
    var o = opts || {};
    if (!geo) return { wIn: num(wIn), lxIn: num(lxIn), lyIn: num(lyIn), snapped: false };

    var aspect = Number(artAspect);
    if (!(isFinite(aspect) && aspect > 0)) aspect = 1;

    var zW = num(geo.zoneWIn);
    var zH = num(geo.zoneHIn);

    // TWO-AXIS CEILING. A wider box is also a taller box, so the zone's bottom
    // edge limits width exactly as its right edge does — the same rule
    // clampWidth already applied on the resize path, missing here.
    //
    // Without it, w could reach zW while h = w/aspect exceeded zH: y then
    // clamped to a max of 0 and the logo hung out of the bottom of its own
    // print area. A 0.9 seed left ~10% slack that usually absorbed the
    // overflow, so this only became visible at the 100% seed (Ugo, screenshot
    // 2026-07-30).
    var maxW = Math.min(zW, zH * aspect);
    var lo = minWidthIn(geo);
    var w = (maxW > lo) ? clamp(num(wIn), lo, maxW) : Math.min(num(wIn) || lo, maxW || lo);
    var h = w / aspect;

    var x = clamp(num(lxIn), 0, Math.max(0, zW - w));
    var y = clamp(num(lyIn), 0, Math.max(0, zH - h));

    var centreX = Math.max(0, zW - w) / 2;
    var tol = snapTolFor(geo, o);
    var dist = Math.abs(x - centreX);

    var snapped = dist < 1e-6;
    if (o.snap && dist < tol) {
      x = centreX;
      snapped = true;
    }

    return { wIn: w, lxIn: x, lyIn: y, snapped: snapped };
  }

  // THE BOUNDARY, in painted-pixel space. One definition, so the rectangle that
  // is DRAWN and the rectangle that is ENFORCED cannot drift apart.
  //
  // They had drifted repeatedly: the box was drawn from percentages while the
  // logo clamped in painted pixels; later the drawn height (px.h) and the real
  // zone height (zoneHIn * pxPerIn) diverged and different call sites picked
  // different ones. Every consumer now asks this function instead of assembling
  // the rectangle itself, and `logoRectPx` below reports the logo in the same
  // space so the two can be compared directly.
  function clampRectPx(geo) {
    if (!geo || !geo.px) return null;
    var ppi = num(geo.pxPerIn);
    if (!(ppi > 0)) return null;
    var w = num(geo.zoneWIn) * ppi;
    var h = num(geo.zoneHIn) * ppi;
    if (!(w > 0 && h > 0)) return null;
    return { x: num(geo.px.x), y: num(geo.px.y), w: w, h: h };
  }

  // The logo's rectangle in the same space, from a clamped placement record.
  function logoRectPx(geo, rec) {
    if (!geo || !geo.px || !rec) return null;
    var ppi = num(geo.pxPerIn);
    if (!(ppi > 0)) return null;
    var aspect = Number(rec.aspect);
    if (!(isFinite(aspect) && aspect > 0)) aspect = 1;
    var w = num(rec.wIn) * ppi;
    return {
      x: num(geo.px.x) + num(rec.lxIn) * ppi,
      y: num(geo.px.y) + num(rec.lyIn) * ppi,
      w: w,
      h: w / aspect
    };
  }

  // The resize ceiling, with its two-axis limit: a wider box also gets taller,
  // so the bottom edge of the zone constrains width just as the right edge does.
  function clampWidth(wIn, lxIn, lyIn, artAspect, geo) {
    if (!geo) return num(wIn);
    var aspect = Number(artAspect);
    if (!(isFinite(aspect) && aspect > 0)) aspect = 1;

    var zW = num(geo.zoneWIn);
    var zH = num(geo.zoneHIn);

    var byRight = zW - num(lxIn);
    var byBottom = (zH - num(lyIn)) * aspect;
    var hi = Math.min(zW, byRight, byBottom);
    var lo = minWidthIn(geo);

    if (!(hi > lo)) return lo;
    return clamp(num(wIn), lo, hi);
  }

  // Rotate a pointer DELTA into the zone's unrotated frame, so drag maths stays
  // axis-aligned on rotated sleeve and cap zones (37-RESEARCH.md Pitfall 5).
  //
  // This rotates the delta, NOT the logo. The logo never turns relative to its
  // zone - there is no user-facing rotation control anywhere in this phase, and
  // this function introduces no angle of its own. It only undoes an angle the
  // merchant already baked into the zone via the mapper.
  function rotateDelta(dx, dy, angleDeg) {
    var a = Number(angleDeg);
    if (!isFinite(a) || a === 0) return { dx: num(dx), dy: num(dy) };
    var r = (-a * Math.PI) / 180;
    var c = Math.cos(r), s = Math.sin(r);
    return { dx: num(dx) * c - num(dy) * s, dy: num(dx) * s + num(dy) * c };
  }

  function isStandard(place, std, tolIn) {
    if (!place || !std) return false;
    var t = typeof tolIn === 'number' ? tolIn : DEFAULTS.stdTolIn;
    return Math.abs(num(place.wIn) - num(std.wIn)) <= t &&
           Math.abs(num(place.lxIn) - num(std.lxIn)) <= t &&
           Math.abs(num(place.lyIn) - num(std.lyIn)) <= t;
  }

  // ---------------------------------------------------------------------------
  // Display + input
  // ---------------------------------------------------------------------------

  // The separator comes from the decimal_separator locale key, never from
  // sniffing navigator.language: the site's /fr/ route is the source of truth
  // for which language the page is in, not the visitor's browser settings.
  function formatMeasure(valueIn, unit, decimalSep) {
    var v = fromInches(valueIn, unit);
    var s = (isFinite(v) ? v : 0).toFixed(1);
    var sep = decimalSep == null ? '.' : String(decimalSep);
    return s.split('.').join(sep);
  }

  // The phase's input-validation boundary: the ONLY path by which customer-typed
  // text becomes a number. Must never return NaN, Infinity, or a negative - the
  // caller clamps afterwards, but a bad value must not get that far.
  function parseMeasure(raw, unit) {
    if (raw == null) return null;
    var s = String(raw).trim().split(',').join('.');
    if (s === '') return null;
    var n = parseFloat(s);
    if (!isFinite(n) || n <= 0) return null;
    var u = String(unit || '').toLowerCase();
    if (u === 'cm') return n / CM_PER_IN;
    return n;
  }

  // Package a placement for the compositor.
  //
  // CONTRACT: the compositor receives zone-relative INCHES plus the zone's width
  // in inches, and recomputes the zone rect itself from the same rawZone. The
  // browser therefore never needs to know the compositor's canvas height (which
  // compositeMockup derives internally from the garment's natural aspect), so
  // preview and bake cannot diverge on where the zone is. Rounded to four
  // decimals so the compositor cache key is stable across float noise.
  function toCompositorRect(place, geo) {
    return {
      lxIn: round4(place && place.lxIn),
      lyIn: round4(place && place.lyIn),
      wIn: round4(place && place.wIn),
      zoneWIn: round4(geo && geo.zoneWIn)
    };
  }

  // ---------------------------------------------------------------------------
  // The cart boundary
  // ---------------------------------------------------------------------------

  // WHAT A PLACEMENT RECORD IS ALLOWED TO TELL THE CART, and the only code that
  // decides it.
  //
  // These properties leave the browser, land on a Shopify order, and are read by
  // the people who print the garment. A number here is an instruction to a
  // press. That is the entire reason this function exists rather than a `toFixed`
  // at each of the three call sites it replaced (single-builder.js twice,
  // bundle-builder-2step.js once): three copies of a safety rule is three
  // chances to update two of them.
  //
  // THE RULE: a physical dimension is emitted only for a record whose zone was
  // actually measured. `rec.measured !== true` — not `=== false` — so a record
  // that lost the flag in localStorage, in a hand-edited blob, or through a
  // future refactor FAILS CLOSED. The cost of failing closed is a marker where a
  // measurement could have been; the cost of failing open is a wrong number
  // printed on a customer's uniform.
  //
  // An unmeasured placement is MARKED, not omitted. Omitting would be safe but
  // ambiguous: absence already means "the customer never sized this placement,
  // bake it at the historic contain fit", and it is the shape both builders
  // already emit for that case. A customer who dragged and resized a logo on an
  // unmeasured zone did size it — the bake honours their placement (the
  // compositor works in RATIOS, see toCompositorRect, so it is exactly right)
  // and the mockup on the order line shows it. What is missing is only the
  // physical figure. The marker says that, in a word no parser can mistake for a
  // dimension: it starts with a letter, so parseFloat yields NaN, and it matches
  // no "<w>x<h> in" pattern.
  var UNMEASURED_MARK = 'unmeasured';

  function cartMeasureFields(rec) {
    if (!rec) return null;
    var w = Number(rec.wIn);
    // No usable width means the customer never sized this placement at all.
    // Null, and the caller emits nothing — the historic behaviour, unchanged.
    if (!(isFinite(w) && w > 0)) return null;

    var aspect = Number(rec.aspect);
    if (!(isFinite(aspect) && aspect > 0)) aspect = 1;
    var h = w / aspect;
    var lx = Number(rec.lxIn); if (!isFinite(lx)) lx = 0;
    var ly = Number(rec.lyIn); if (!isFinite(ly)) ly = 0;

    if (rec.measured !== true) {
      return {
        measured: false,
        size: UNMEASURED_MARK,
        pos: UNMEASURED_MARK,
        // No unit token either. `_logo_unit_N` records which unit the customer
        // was READING; on an unmeasured zone they were reading none, and
        // emitting 'po' beside a marker would half-restore the fiction.
        unit: null,
        // The customer-facing line is suppressed entirely rather than marked.
        // The private keys are admin-only and the art team needs the signal;
        // "unmeasured" on a cart page would alarm a customer and tell them
        // nothing they can act on.
        wIn: null,
        hIn: null
      };
    }

    var n = function (v) { return Number(v).toFixed(1); };
    return {
      measured: true,
      // TRUE INCHES at one decimal, never the display unit: the catalogue mixes
      // CENTIMETERS and INCHES print areas and toInches normalised both long
      // before the record was written.
      size: n(w) + 'x' + n(h) + ' in',
      pos: n(lx) + ',' + n(ly) + ' in',
      unit: rec.unit === 'cm' ? 'cm' : 'po',
      wIn: w,
      hIn: h
    };
  }

  // The customer-visible size line, or null when there is nothing honest to say.
  // Returning null rather than an empty string forces the caller to branch — an
  // empty string would happily become a blank cart property.
  function cartMeasureLabel(rec, unit, decimalSep, unitWord) {
    var f = cartMeasureFields(rec);
    if (!f || !f.measured) return null;
    var u = unit === 'cm' ? 'cm' : 'po';
    return formatMeasure(f.wIn, u, decimalSep) + ' × '
      + formatMeasure(f.hIn, u, decimalSep) + ' ' + String(unitWord == null ? u : unitWord);
  }

  // ---------------------------------------------------------------------------
  // Assertions
  // ---------------------------------------------------------------------------

  function __selfTest() {
    var failures = [];
    var passed = 0;

    // Records rather than throws, so one failure does not hide the rest.
    function ok(cond, label) {
      if (cond) { passed++; } else { failures.push(label); }
    }
    function near(a, b, tol, label) {
      var d = Math.abs(Number(a) - Number(b));
      ok(isFinite(d) && d <= tol, label + ' (got ' + a + ', want ' + b + ' +/-' + tol + ')');
    }

    // -- units --------------------------------------------------------------
    near(toInches(30, 'CENTIMETERS'), 11.811, 0.001, 'toInches cm');
    ok(toInches(10, 'INCHES') === 10, 'toInches inches is exact');
    near(toInches(300, 'MILLIMETERS'), 11.811, 0.001, 'toInches mm');
    near(toInches(1, 'METERS'), 39.37, 0.01, 'toInches m');
    ok(toInches(30, '') === toInches(30, 'CENTIMETERS'), 'toInches empty unit falls back to cm');
    ok(toInches(30, 'FURLONGS') === toInches(30, 'CENTIMETERS'), 'toInches unknown unit falls back to cm');
    ok(toInches(NaN, 'CENTIMETERS') === 0, 'toInches NaN is 0');
    ok(toInches(Infinity, 'CENTIMETERS') === 0, 'toInches Infinity is 0');
    ok(toInches(-5, 'CENTIMETERS') === 0, 'toInches negative is 0');
    near(fromInches(11, 'cm'), 27.94, 0.001, 'fromInches cm');
    ok(fromInches(11, 'po') === 11, 'fromInches inches is identity');

    // -- zonePx mirrors the compositor ---------------------------------------
    // Recomputed here the way builder-shared-services.js:467-484 does, so the
    // two implementations are pinned together rather than merely believed equal.
    var rz = { top: '25%', left: '50%', w: '34%', h: '50.37%' };
    var zp = zonePx(rz, 600, 720);
    var expW = 600 * 0.34;
    var expH = 600 * 0.5037;
    near(zp.w, expW, 0.01, 'zonePx width mirrors compositor');
    near(zp.h, expH, 0.01, 'zonePx height is a fraction of box WIDTH');
    near(zp.x, 600 * 0.5 - expW / 2, 0.01, 'zonePx x centres on left%');
    near(zp.y, 720 * 0.25, 0.01, 'zonePx y is a fraction of box HEIGHT');
    ok(zonePx({ w: 204, h: 272 }, 600, 720).w === 204, 'zonePx legacy 600px numbers scale 1:1 at 600');
    ok(zonePx(null, 600, 720).w > 0, 'zonePx survives a null zone');
    ok(zonePx({ left: 'x', top: 'y', w: '10%' }, 600, 720).x === 600 * 0.5 - 30,
      'zonePx defaults left to 50% on a bad parse');

    // -- zoneGeometry ---------------------------------------------------------
    // THE CONTRACT CHANGED TWICE ON 2026-08-02, and the second change is not a
    // retreat from the first.
    //
    // Morning: the shared print_areas metaobject stopped being able to size
    // anything, so a zone with no typed wIn had no measurement and resolved to
    // NULL. That fixed the fabricated-inches bug and introduced a worse one —
    // both builders read null as "no geometry" and dropped to a legacy overlay
    // with pointer-events:none, so 676 of the store's 680 zones lost drag and
    // resize entirely.
    //
    // Afternoon: an unmeasured zone RESOLVES, in zone-width space, flagged
    // measured:false. The catalogue still cannot size anything — that is what
    // the assertions below check, and it is the whole point — but "we do not
    // know this zone's inches" no longer costs the customer the ability to move
    // their logo.
    //
    // NULL NOW MEANS ONE THING ONLY: there is no usable RECTANGLE. Not "no
    // measurement" — a missing measurement is a labelling fact, and it is
    // expressed by the flag, not by an absent object.
    ok(zoneGeometry({ maxWidth: 30, maxWidthUnit: 'CENTIMETERS' }, { w: '0%', wIn: 6 }, 600, 720) === null,
      'zoneGeometry null on a zero-width zone');
    ok(zoneGeometry({ maxWidth: 12, maxWidthUnit: 'INCHES' }, { w: '0%' }, 600, 720) === null,
      'zoneGeometry null on a zero-width zone even when unmeasured');
    ok(zoneGeometry({ maxWidth: 12, maxWidthUnit: 'INCHES' }, rz, 0, 0) === null,
      'zoneGeometry null when the box it is measured into has no width');

    // A USELESS CATALOGUE ENTRY IS NO LONGER FATAL, because the catalogue no
    // longer participates in sizing. These three used to return null for the
    // wrong reason — the area's maxWidth was the scale — and each must now
    // resolve as an ordinary unmeasured zone. If any of them ever returns null
    // again, the shared-metaobject dependency has been reintroduced.
    var junkAreas = [
      { maxWidth: 0, maxWidthUnit: 'CENTIMETERS' },
      { maxWidth: -3, maxWidthUnit: 'CENTIMETERS' },
      { maxWidth: 'abc', maxWidthUnit: 'CENTIMETERS' }
    ];
    var junkAreaOk = true;
    for (var jai = 0; jai < junkAreas.length; jai++) {
      var jag = zoneGeometry(junkAreas[jai], rz, 600, 720);
      if (!jag || jag.measured !== false) junkAreaOk = false;
    }
    ok(junkAreaOk, 'an unusable area maxWidth no longer decides anything: still an unmeasured zone');

    // A perfectly valid catalogue entry, and a zone that simply never had its
    // real size typed in the mapper — the shape 90 of the store's 92 configured
    // products are in. THE REGRESSION GUARD for the shared-metaobject bug is
    // that its width is NOT 12: it is the drawn box's own pixel width.
    var fixZone = { top: '18.5185%', left: '50%', w: '34%', h: '45.3333%' };
    var fixArea = { maxWidth: 12, maxWidthUnit: 'INCHES', maxHeight: 16, maxHeightUnit: 'INCHES' };
    var unmGeo = zoneGeometry(fixArea, fixZone, 600, 540);
    ok(unmGeo !== null, 'an untyped zone RESOLVES, so its logo stays draggable');
    if (unmGeo) {
      ok(unmGeo.measured === false, 'an untyped zone is flagged unmeasured');
      ok(unmGeo.scaleSource === 'drawn', 'an untyped zone reports the drawn box as its scale');
      ok(unmGeo.zoneWIn !== 12, 'an untyped zone does NOT inherit the shared area width');
      ok(unmGeo.zoneWIn === 1, 'an untyped zone is exactly one zone wide, by definition');
      near(unmGeo.zoneHIn, unmGeo.px.h / unmGeo.px.w, 1e-9,
        'and its height is the drawn box\'s aspect, in the same unit');
      near(unmGeo.pxPerIn, unmGeo.px.w, 1e-9,
        'the scale is the rendered box width, so the record stays surface-free');
      near(unmGeo.areaWIn, 12, 0.01, 'the catalogue width is still REPORTED, it just cannot size');
      ok(unmGeo.widthMismatch === false,
        'no width cross-check on an unmeasured zone — there is no second figure to disagree');
      ok(unmGeo.heightMismatch === false, 'and no height cross-check either');
      // The rectangle is the whole point: a drawn box that resolves must be
      // drawable, or the boundary the customer clamps against does not exist.
      var unmBox = clampRectPx(unmGeo);
      ok(unmBox !== null, 'an unmeasured zone still yields a drawable boundary');
      if (unmBox) {
        near(unmBox.w, unmGeo.px.w, 1e-9, 'the unmeasured boundary is the drawn box, exactly');
        near(unmBox.h, unmGeo.px.h, 1e-9, 'on both axes');
      }
    }

    // A typed hIn WITHOUT a typed wIn is half a measurement and must be ignored
    // for sizing. Honouring it would put inches on one axis of a pixel box, and
    // every aspect-derived ceiling in clampPlace/clampWidth would be wrong by
    // whatever pxPerIn happened to be.
    var halfMeasured = zoneGeometry(fixArea,
      { top: '18.5185%', left: '50%', w: '34%', h: '45.3333%', hIn: 16 }, 600, 540);
    ok(halfMeasured && halfMeasured.measured === false,
      'a height with no width is not a measurement');
    ok(halfMeasured && halfMeasured.heightSource === 'drawn',
      'and it does not become the zone height');
    ok(halfMeasured && Math.abs(halfMeasured.zoneHIn - halfMeasured.px.h / halfMeasured.px.w) < 1e-9,
      'the drawn box governs both axes when the width was never typed');

    // The same fixture WITH its real size typed: 204px over 12in is 17px/in,
    // and the 272px box is 16in tall. Reproduces the design prototype's numbers
    // without either figure living in the module.
    var typedZone = { top: '18.5185%', left: '50%', w: '34%', h: '45.3333%', wIn: 12 };
    var geo = zoneGeometry(fixArea, typedZone, 600, 540);
    ok(geo !== null, 'zoneGeometry fixture resolves once the zone is measured');
    if (geo) {
      near(geo.px.w, 204, 0.01, 'fixture zone is 204px wide');
      near(geo.px.h, 272, 0.01, 'fixture zone is 272px tall');
      near(geo.pxPerIn, 17, 0.01, 'fixture scale is 17px per inch');
      ok(geo.zoneWIn === 12, 'fixture zone width is 12in');
      near(geo.zoneHIn, 16, 0.01, 'fixture derived zone height is 16in');
      ok(geo.scaleSource === 'zone', 'scale always comes from the zone now');
      ok(geo.measured === true, 'a typed width is a real measurement and says so');
      ok(geo.heightSource === 'drawn', 'height falls back to the DRAWN box, never the area');
      ok(geo.heightMismatch === false, 'fixture declared and derived heights agree');
    }

    // THE S05610 CASE, in miniature. A square drawn box on a catalogue entry
    // declaring a 1:3.125 aspect. Before 2026-08-02 the area's aspect was scaled
    // onto the zone and the square rendered as a sliver; now the drawn shape is
    // honoured and the catalogue aspect only raises heightMismatch.
    var squareZone = { top: '10%', left: '50%', w: '20%', h: '20%', wIn: 3.5 };
    var sliverArea = { maxWidth: 8, maxWidthUnit: 'CENTIMETERS',
                       maxHeight: 25, maxHeightUnit: 'CENTIMETERS' };
    var sq = zoneGeometry(sliverArea, squareZone, 600, 600);
    ok(sq !== null, 'a measured square zone resolves against a lopsided area');
    if (sq) {
      near(sq.zoneWIn, 3.5, 0.001, 'the typed width wins over the catalogue 8cm');
      near(sq.zoneHIn, 3.5, 0.001, 'a square drawn box stays SQUARE — no area aspect');
      ok(sq.heightSource === 'drawn', 'height came from the drawn box');
      ok(sq.heightMismatch === true, 'the catalogue disagreement is still surfaced');
    }

    var badGeo = zoneGeometry(
      { maxWidth: 12, maxWidthUnit: 'INCHES', maxHeight: 4, maxHeightUnit: 'INCHES' },
      typedZone, 600, 540);
    ok(badGeo && badGeo.heightMismatch === true, 'heightMismatch flags a disagreeing drawn box');
    ok(badGeo && Math.abs(badGeo.zoneHIn - 16) < 0.01,
      'the DRAWN box wins over the declared area aspect');
    ok(badGeo && Math.abs(badGeo.drawnHIn - 16) < 0.01, 'the drawn height is still reported separately');
    ok(badGeo && badGeo.boxHpx > 0, 'boxHpx gives the renderable zone height in pixels');

    // -- per-zone real-world calibration (plan 37-14) --------------------------
    var calZone = { top: '18.5185%', left: '50%', w: '34%', h: '45.3333%', wIn: 6 };
    var calArea = { maxWidth: 12, maxWidthUnit: 'INCHES', maxHeight: 16, maxHeightUnit: 'INCHES' };
    var calGeo = zoneGeometry(calArea, calZone, 600, 540);
    ok(calGeo !== null, 'a calibrated zone resolves');
    if (calGeo) {
      near(calGeo.pxPerIn, 204 / 6, 0.01, 'zone wIn sets the scale, not the area maxWidth');
      ok(calGeo.zoneWIn === 6, 'zoneWIn is the per-zone measurement');
      ok(calGeo.scaleSource === 'zone', 'scaleSource reports the zone won');
      near(calGeo.areaWIn, 12, 0.01, 'the area width is still reported for comparison');
      ok(calGeo.widthMismatch === true, 'a 6in zone against a 12in area is flagged');
    }

    var calH = zoneGeometry(calArea, { top: '25%', left: '50%', w: '34%', h: '45%', wIn: 12, hIn: 9 }, 600, 540);
    ok(calH && Math.abs(calH.zoneHIn - 9) < 0.0001, 'zone hIn is used verbatim as the zone height');
    ok(calH && calH.drawnHIn !== calH.zoneHIn, 'the drawn height is still reported separately');

    // THE CONTRACT THAT MATTERS MOST, stated in the one place a reader will look
    // for it: an uncalibrated zone must not inherit the shared metaobject's
    // width, AND it must still resolve. Both halves, together, because each on
    // its own has already shipped as a bug — the inheritance until 2026-08-02
    // morning, the null for the rest of that day.
    var plainZone = { top: '18.5185%', left: '50%', w: '34%', h: '45.3333%' };
    var plainGeo = zoneGeometry(calArea, plainZone, 600, 540);
    ok(plainGeo !== null, 'an uncalibrated zone resolves');
    ok(plainGeo && plainGeo.zoneWIn !== 12,
      'an uncalibrated zone does not inherit the area width');
    ok(plainGeo && plainGeo.measured === false,
      'and it is flagged unmeasured so nothing labels its numbers');

    // Junk wIn is not a measurement either, so it takes the same path as a
    // missing one: an unmeasured zone, never a bogus scale. The assertion's
    // point has never changed — junk must not produce a physical number — only
    // the shape of "no physical number" has.
    var junk = [0, -3, 'abc', null, Infinity, NaN];
    var junkOk = true;
    for (var ji = 0; ji < junk.length; ji++) {
      var jz = { top: '25%', left: '50%', w: '34%', h: '45%', wIn: junk[ji] };
      var jg = zoneGeometry(calArea, jz, 600, 540);
      // Resolves, is flagged, and — the part that matters — its width is the
      // drawn box itself rather than the catalogue's 12 or the junk value.
      if (!jg || jg.measured !== false || jg.zoneWIn !== 1) junkOk = false;
    }
    ok(junkOk, 'a zero, negative or non-numeric wIn yields an unmeasured zone, never a fallback scale');

    // -- minWidthIn -----------------------------------------------------------
    ok(minWidthIn({ zoneWIn: 12 }) === Math.max(DEFAULTS.minFloorIn, 12 * DEFAULTS.minZoneFrac),
      'minWidthIn scales with the zone');
    ok(minWidthIn({ zoneWIn: 4 }) === DEFAULTS.minFloorIn, 'minWidthIn floors on a small zone');
    ok(minWidthIn({ zoneWIn: 0.2 }) <= 0.2, 'minWidthIn never exceeds the zone width');
    // An absent flag reads as MEASURED inside this module — a partial geometry
    // from a caller keeps the historic floor. The opposite polarity governs the
    // cart, where an absent flag must fail closed.
    ok(minWidthIn({ zoneWIn: 4, measured: true }) === DEFAULTS.minFloorIn,
      'an explicitly measured small zone keeps the physical floor');
    ok(minWidthIn({ zoneWIn: 4 }) === minWidthIn({ zoneWIn: 4, measured: true }),
      'an absent measured flag is treated as measured, not as unmeasured');
    // Unconverted, a 0.75 in floor in a space whose whole zone is 1 unit wide
    // would forbid any logo under three quarters of the print area. Converted,
    // it lands under the proportional floor and the proportion governs — which
    // is the only rule that means anything without a real measurement.
    ok(minWidthIn({ zoneWIn: 1, measured: false }) === DEFAULTS.minZoneFrac,
      'an unmeasured zone floors proportionally, never at an inch constant');
    ok(minWidthIn({ zoneWIn: 1, measured: false }) < 1,
      'the unmeasured floor never reaches the zone width');

    // -- inSpace ---------------------------------------------------------------
    // The single conversion every physical tunable goes through. Asserted on its
    // own because three helpers and one clamp depend on it, and a sign or a
    // reciprocal error in here would show up as four unrelated-looking bugs.
    ok(inSpace({ measured: true, zoneWIn: 12 }, 0.25) === 0.25,
      'inSpace is identity on a measured geometry');
    ok(inSpace(null, 0.25) === 0.25, 'and on no geometry at all');
    ok(inSpace({ zoneWIn: 12 }, 0.25) === 0.25, 'and on a geometry with no flag');
    near(inSpace({ measured: false, zoneWIn: 1 }, DEFAULTS.snapTolIn),
      DEFAULTS.snapTolIn / REFERENCE_ZONE_IN, 1e-12,
      'an unmeasured zone keeps the tunable\'s PROPORTION of the reference zone');
    ok(inSpace({ measured: false, zoneWIn: 1 }, 0) === 0, 'zero converts to zero');
    ok(inSpace({ measured: false, zoneWIn: 1 }, REFERENCE_ZONE_IN) === 1,
      'a tunable the size of the reference zone becomes the size of this one');

    // -- slider range arithmetic ----------------------------------------------
    // Endpoints passed in, never assumed: 2 -> 12 here is a FIXTURE, the same
    // way the 204px zone above is a fixture. The shipped range comes from the
    // active area.
    near(fractionToInches(0, 2, 12), 2, 1e-9, 'fractionToInches 0 maps to the minimum');
    near(fractionToInches(1, 2, 12), 12, 1e-9, 'fractionToInches 1 maps to the maximum');
    near(fractionToInches(0.5, 2, 12), 7, 1e-9, 'fractionToInches 0.5 maps to the midpoint');
    ok(fractionToInches(-3, 2, 12) === 2, 'fractionToInches clamps a press left of the track');
    ok(fractionToInches(9, 2, 12) === 12, 'fractionToInches clamps a press right of the track');
    ok(fractionToInches(NaN, 2, 12) === 2, 'fractionToInches falls back to the minimum on junk');
    ok(fractionToInches(0.5, 12, 12) === 12, 'fractionToInches returns the floor on a zero-width range');
    ok(fractionToInches(0.5, 12, 2) === 12, 'fractionToInches returns the floor on an inverted range');
    near(inchesToFraction(2, 2, 12), 0, 1e-9, 'inchesToFraction is 0 at the minimum');
    near(inchesToFraction(12, 2, 12), 1, 1e-9, 'inchesToFraction is 1 at the maximum');
    near(inchesToFraction(7, 2, 12), 0.5, 1e-9, 'inchesToFraction is 0.5 at the midpoint');
    ok(inchesToFraction(-5, 2, 12) === 0, 'inchesToFraction clamps a below-range value');
    ok(inchesToFraction(99, 2, 12) === 1, 'inchesToFraction clamps an above-range value');
    ok(inchesToFraction(7, 12, 12) === 0, 'inchesToFraction is 0 on a zero-width range');
    ok(inchesToFraction(NaN, 2, 12) === 0, 'inchesToFraction is 0 on junk');

    // The property that matters: the thumb lands where the value is. Asserted
    // against a per-area range (0.75in floor from minWidthIn's own default)
    // rather than the fixture, so the pair is proven on real endpoints too.
    var rtOk = true;
    for (var si = 0; si <= 10; si++) {
      var wRt = fractionToInches(si / 10, DEFAULTS.minFloorIn, 12);
      if (Math.abs(inchesToFraction(wRt, DEFAULTS.minFloorIn, 12) - si / 10) > 1e-9) rtOk = false;
    }
    ok(rtOk, 'fraction and inches round-trip across the whole track');

    // -- standardPlacement ----------------------------------------------------
    if (geo) {
      var stdWide = standardPlacement(geo, 2.381, 'top', 'center');
      near(stdWide.wIn, 12 * DEFAULTS.stdFitFrac, 0.01, 'standard fit leaves a margin on a wide zone');
      near(stdWide.lxIn, (12 - 12 * DEFAULTS.stdFitFrac) / 2, 0.01, 'standard wide fit centres its margin');
      near(stdWide.lyIn, 0, 0.01, 'standard top anchor sits at the zone top');

      // A tall, narrow artwork is constrained by HEIGHT instead, which is the
      // case the design prototype's 11in/0.5in/1.5in fixture describes.
      var stdTall = standardPlacement(geo, 0.5, 'center', 'center');
      ok(stdTall.wIn < 12, 'a tall artwork is height-constrained, not width-constrained');
      near(stdTall.lyIn, (16 - stdTall.wIn / 0.5) / 2, 0.01, 'centre anchor halves the vertical leftover');
      ok(stdTall.wIn > 0, 'a tall artwork still gets a positive width');

      // The general property, asserted instead of the prototype constant: the
      // box always fits inside the zone on both axes and touches at least one edge.
      var aspects = [2.381, 1, 0.5, 4, 0.25];
      var vs = ['top', 'center', 'bottom'];
      var hs = ['left', 'center', 'right'];
      var i, j, k, fits = true, touches = true;
      for (i = 0; i < aspects.length; i++) {
        for (j = 0; j < vs.length; j++) {
          for (k = 0; k < hs.length; k++) {
            var p = standardPlacement(geo, aspects[i], vs[j], hs[k]);
            var ph = p.wIn / aspects[i];
            if (p.wIn - 12 > 0.01 || ph - 16 > 0.01 ||
                p.lxIn < -0.01 || p.lyIn < -0.01 ||
                p.lxIn + p.wIn - 12 > 0.01 || p.lyIn + ph - 16 > 0.01) fits = false;
            var tw = Math.abs(p.wIn - 12 * DEFAULTS.stdFitFrac) < 0.01;
            var th = Math.abs(ph - 16 * DEFAULTS.stdFitFrac) < 0.01;
            if (!tw && !th) touches = false;
          }
        }
      }
      ok(fits, 'standardPlacement always fits inside the zone on both axes');
      ok(touches, 'standardPlacement always fills stdFitFrac of one axis');
    }

    // -- clampPlace -----------------------------------------------------------
    var g = { zoneWIn: 12, zoneHIn: 16, pxPerIn: 17, px: { w: 204, h: 272 } };
    var farLeft = clampPlace(-99, 4, 6, 2, g, {});
    ok(farLeft.lxIn === 0, 'clampPlace pins the left edge');
    var farRight = clampPlace(99, 4, 6, 2, g, {});
    near(farRight.lxIn, 6, 0.001, 'clampPlace pins the right edge');
    var farUp = clampPlace(3, -99, 6, 2, g, {});
    ok(farUp.lyIn === 0, 'clampPlace pins the top edge');
    var farDown = clampPlace(3, 99, 6, 2, g, {});
    near(farDown.lyIn, 16 - 3, 0.001, 'clampPlace pins the bottom edge');

    var inTol = clampPlace(3 + 0.1, 2, 6, 2, g, { snap: true });
    near(inTol.lxIn, 3, 0.001, 'clampPlace snaps to centre inside the tolerance');
    ok(inTol.snapped === true, 'clampPlace reports snapped inside the tolerance');
    var outTol = clampPlace(3 + 0.4, 2, 6, 2, g, { snap: true });
    near(outTol.lxIn, 3.4, 0.001, 'clampPlace leaves the value alone outside the tolerance');
    ok(outTol.snapped === false, 'clampPlace reports not snapped outside the tolerance');
    var overridden = clampPlace(3 + 0.4, 2, 6, 2, g, { snap: true, snapTolIn: 0.5 });
    near(overridden.lxIn, 3, 0.001, 'clampPlace honours an overridden snap tolerance');
    ok(clampPlace(3, 2, 999, 2, g, {}).wIn === 12, 'clampPlace clamps width to the zone');
    ok(clampPlace(3, 2, 0.0001, 2, g, {}).wIn === minWidthIn(g), 'clampPlace clamps width to the floor');

    // THE DRAWN BOUNDARY IS THE ENFORCED BOUNDARY.
    //
    // Ugo, 2026-07-30: "the blue dotted box needs to be at the same place as the
    // print area that is showing the limits — if we can drag the logo outside
    // the zone it will confuse the customer." This asserts it in pixel space,
    // which is where the customer actually sees it: whatever clampPlace permits
    // must sit inside the rectangle clampRectPx draws. Any future call site that
    // reassembles the boundary from px.h, a percentage, or anything else will
    // fail here rather than on Ugo's screen.
    (function () {
      var geos = [
        // typed width, square area drawn wide
        zoneGeometry({ maxWidth: 10, maxWidthUnit: 'CENTIMETERS', maxHeight: 10, maxHeightUnit: 'CENTIMETERS' },
          { top: '20%', left: '50%', w: '30%', h: '18%', wIn: 3.5 }, 600, 600),
        // tall area drawn short — typed, because since 2026-08-02 an untyped
        // zone has no measurement and would drop out of this sweep entirely
        zoneGeometry({ maxWidth: 25, maxWidthUnit: 'CENTIMETERS', maxHeight: 30, maxHeightUnit: 'CENTIMETERS' },
          { top: '15%', left: '50%', w: '45%', h: '30%', wIn: 9.84 }, 600, 720),
        // explicit per-zone height
        zoneGeometry({ maxWidth: 12, maxWidthUnit: 'INCHES', maxHeight: 16, maxHeightUnit: 'INCHES' },
          { top: '10%', left: '40%', w: '50%', h: '40%', wIn: 12, hIn: 16 }, 800, 800)
      ].filter(Boolean);
      ok(geos.length === 3, 'boundary sweep built all three geometries');

      var aspects = [0.3, 0.8, 1, 1.4, 2.5, 6];
      var spots = [-999, 0, 1.5, 999];
      var widths = [0.1, 1, 4, 999];
      var checked = 0, outside = 0, boxMismatch = 0;

      for (var gi = 0; gi < geos.length; gi++) {
        var g = geos[gi];
        var box = clampRectPx(g);
        if (!box) { ok(false, 'clampRectPx returned null for a valid geometry'); continue; }
        // The drawn rectangle must equal the enforced one, in pixels.
        if (Math.abs(box.w - g.px.w) > 0.001 || Math.abs(box.h - g.boxHpx) > 0.001) boxMismatch++;

        for (var ai = 0; ai < aspects.length; ai++) {
          for (var si = 0; si < spots.length; si++) {
            for (var sj = 0; sj < spots.length; sj++) {
              for (var wi = 0; wi < widths.length; wi++) {
                var p = clampPlace(spots[si], spots[sj], widths[wi], aspects[ai], g, { snap: true });
                var r = logoRectPx(g, { wIn: p.wIn, lxIn: p.lxIn, lyIn: p.lyIn, aspect: aspects[ai] });
                checked++;
                if (!r) { outside++; continue; }
                if (r.x - box.x < -0.001 || r.y - box.y < -0.001
                  || (r.x + r.w) - (box.x + box.w) > 0.001
                  || (r.y + r.h) - (box.y + box.h) > 0.001) outside++;
              }
            }
          }
        }
      }

      ok(checked === 3 * aspects.length * spots.length * spots.length * widths.length,
        'boundary sweep ran every combination');
      ok(boxMismatch === 0, 'the drawn rectangle equals the enforced rectangle for every geometry');
      ok(outside === 0, 'a clamped logo never leaves the drawn boundary, in pixels');
    })();

    // THE BOUNDARY MATCHES THE DRAWN BOX, not the shared catalogue entry.
    //
    // THIS ASSERTION IS THE REVERSE OF WHAT IT SAID BEFORE, and the reversal is
    // deliberate. On 2026-07-30 the rule was "the boundary must match the
    // CONFIGURED print area, not the hand-drawn box", because a square 10x10cm
    // area drawn as a wide rectangle reported a non-square boundary.
    //
    // That rule assumed the catalogue figure described THIS product. It does
    // not: one print_areas metaobject is shared by up to 70 products, so the
    // "configured" area is whichever garment happened to be measured first.
    // Applying its aspect to every product is what rendered S05610's hand-drawn
    // SQUARE sleeve at 1:3.125 (Ugo, 2026-08-02). Per his call, the catalogue no
    // longer sizes anything; the mapper is the only source of physical size.
    //
    // The 2026-07-30 concern is not dismissed, it is REDIRECTED: a drawn box
    // that disagrees with the catalogue still raises heightMismatch, so a
    // mis-drawn zone stays visible — it just no longer gets silently overridden.
    (function () {
      // Square area (10x10 cm), deliberately drawn as a WIDE rectangle, with a
      // typed width but no typed height.
      var gSq = zoneGeometry(
        { maxWidth: 10, maxWidthUnit: 'CENTIMETERS', maxHeight: 10, maxHeightUnit: 'CENTIMETERS' },
        { top: '20%', left: '50%', w: '40%', h: '20%', wIn: 3.5 },
        600, 600
      );
      if (!gSq) { ok(false, 'drawn-box height: geometry did not build'); return; }
      near(gSq.zoneWIn, 3.5, 0.001, 'typed zone width is used verbatim');
      near(gSq.zoneHIn, 3.5 * 0.5, 0.001,
        'a zone drawn 2:1 reports a 2:1 boundary, whatever the catalogue says');
      ok(gSq.heightSource === 'drawn', 'height is reported as coming from the drawn box');
      ok(gSq.heightMismatch === true,
        'and the disagreement with the square catalogue area is still surfaced');

      // Non-square area (30 wide x 35 tall cm): the catalogue aspect is ignored
      // for sizing and the drawn box governs.
      var gTall = zoneGeometry(
        { maxWidth: 30, maxWidthUnit: 'CENTIMETERS', maxHeight: 35, maxHeightUnit: 'CENTIMETERS' },
        { top: '20%', left: '50%', w: '40%', h: '10%', wIn: 12 },
        600, 600
      );
      near(gTall.zoneHIn, 12 * (10 / 40), 0.001,
        'the DRAWN aspect governs, not the declared area aspect');

      // An explicit per-zone hIn still wins over everything.
      var gExplicit = zoneGeometry(
        { maxWidth: 10, maxWidthUnit: 'CENTIMETERS', maxHeight: 10, maxHeightUnit: 'CENTIMETERS' },
        { top: '20%', left: '50%', w: '40%', h: '20%', wIn: 3.5, hIn: 2 },
        600, 600
      );
      near(gExplicit.zoneHIn, 2, 0.001, 'an explicit zone hIn outranks the area aspect');
      ok(gExplicit.heightSource === 'zone', 'explicit hIn is reported as the zone source');

      // With no usable declared height, the drawn box is still the last resort.
      var gDrawn = zoneGeometry(
        { maxWidth: 10, maxWidthUnit: 'CENTIMETERS' },
        { top: '20%', left: '50%', w: '40%', h: '20%', wIn: 3.5 },
        600, 600
      );
      ok(gDrawn.heightSource === 'drawn', 'no declared height falls back to the drawn box');
      near(gDrawn.zoneHIn, gDrawn.px.h / gDrawn.pxPerIn, 0.001,
        'the drawn fallback still reports the drawn height');
    })();

    // THE INVARIANT THAT BROKE: the result must fit on BOTH axes, always.
    //
    // clampPlace used to clamp width against the zone width alone, so a logo
    // squatter than its zone reached full width and then overflowed the bottom
    // — the ceiling clampWidth already enforced on the resize path. Nothing
    // asserted it here, which is why the escape shipped. Sweep aspects either
    // side of the zone's own ratio, from every corner and well past every edge.
    (function () {
      var g2 = zoneGeometry(
        { maxWidth: 12, maxWidthUnit: 'INCHES', maxHeight: 16, maxHeightUnit: 'INCHES' },
        // wIn typed: since 2026-08-02 an unmeasured zone yields no geometry, so
        // this fixture must carry its own width or the sweep has nothing to run
        // against. The clamp behaviour under test is unaffected by the value.
        { top: '10%', left: '50%', w: '50%', h: '40%', wIn: 12 },
        600, 720
      );
      if (!g2) { ok(false, 'clampPlace two-axis sweep could not build a geometry'); return; }
      var zW = g2.zoneWIn, zH = g2.zoneHIn;
      var aspects = [0.2, 0.5, 0.9, zW / zH, 1.5, 3, 8];
      var starts = [-999, 0, zW / 2, 999];
      var escaped = 0, checked = 0;
      for (var ai = 0; ai < aspects.length; ai++) {
        for (var si = 0; si < starts.length; si++) {
          for (var sj = 0; sj < starts.length; sj++) {
            var r = clampPlace(starts[si], starts[sj], 999, aspects[ai], g2, {});
            var rh = r.wIn / aspects[ai];
            checked++;
            if (r.wIn - zW > 1e-6 || rh - zH > 1e-6
              || r.lxIn < -1e-6 || r.lyIn < -1e-6
              || (r.lxIn + r.wIn) - zW > 1e-6 || (r.lyIn + rh) - zH > 1e-6) escaped++;
          }
        }
      }
      ok(checked === aspects.length * starts.length * starts.length,
        'clampPlace two-axis sweep ran every combination');
      ok(escaped === 0, 'clampPlace never lets the logo leave the zone on either axis');
    })();
    var input = { lxIn: -99 };
    clampPlace(input.lxIn, 0, 6, 2, g, {});
    ok(input.lxIn === -99, 'clampPlace does not mutate its input');

    // -- clampWidth -----------------------------------------------------------
    ok(clampWidth(999, 0, 0, 2, g) === 12, 'clampWidth ceiling is the zone width');
    near(clampWidth(999, 6, 0, 2, g), 6, 0.001, 'clampWidth respects the right edge');
    near(clampWidth(999, 0, 12, 2, g), 8, 0.001, 'clampWidth respects the bottom edge via the aspect');
    ok(clampWidth(0.0001, 0, 0, 2, g) === minWidthIn(g), 'clampWidth floors at minWidthIn');

    // =========================================================================
    // AN UNMEASURED ZONE IS FULLY INTERACTIVE (quick task 260802-i2c)
    // =========================================================================
    //
    // The whole change in one section. 676 of the live store's 680 zones have no
    // typed width; on 2026-08-02 morning that made every one of them
    // undraggable, because zoneGeometry returned null and both builders fell
    // through to a legacy overlay carrying pointer-events:none.
    //
    // The claim under test is that the pixel-space geometry is not a special
    // case pretending to be the normal one, but genuinely the SAME geometry in a
    // different unit: every clamp, fit and gesture path behaves identically, and
    // the only thing that changes is that nothing may put a unit on the numbers.
    // Both halves are asserted — the interaction, and the silence.
    (function () {
      // The same drawn box, twice: once with a real measurement typed in the
      // mapper, once without. Everything below compares the pair, because
      // "behaves exactly as a measured zone does" is a statement about two
      // objects, not one.
      var drawn = { top: '20%', left: '50%', w: '34%', h: '25%' };
      var area = { maxWidth: 12, maxWidthUnit: 'INCHES', maxHeight: 16, maxHeightUnit: 'INCHES' };
      var mGeo = zoneGeometry(area, { top: '20%', left: '50%', w: '34%', h: '25%', wIn: 12 }, 600, 600);
      var uGeo = zoneGeometry(area, drawn, 600, 600);

      ok(mGeo !== null && uGeo !== null, 'both the measured and the unmeasured fixture resolve');
      if (!mGeo || !uGeo) return;

      ok(uGeo.measured === false && mGeo.measured === true, 'the pair differs only in measuredness');
      near(uGeo.px.x, mGeo.px.x, 1e-9, 'the drawn box lands in the same place either way');
      near(uGeo.px.w, mGeo.px.w, 1e-9, 'and is the same size on screen');
      near(uGeo.px.h, mGeo.px.h, 1e-9, 'on both axes');

      // -- 1. CLAMPABLE, and the boundary is the drawn box ---------------------
      var uBox = clampRectPx(uGeo);
      var mBox = clampRectPx(mGeo);
      ok(uBox !== null, 'an unmeasured zone has an enforceable boundary');
      if (uBox && mBox) {
        near(uBox.x, mBox.x, 1e-9, 'the two boundaries share an origin');
        near(uBox.w, mBox.w, 1e-9, 'and a width in painted pixels');
        near(uBox.h, mBox.h, 1e-9, 'and a height in painted pixels');
      }

      // -- 2. THE LOGO CANNOT ESCAPE ------------------------------------------
      //
      // The identical sweep the measured geometries get, run in zone-width space
      // and taken in PIXELS — the space the customer's eye is in, and the only one
      // in which the two geometries are directly comparable. Ranges are given as
      // fractions of each zone so "well past every edge" means the same in both.
      var aspects = [0.25, 0.6, 1, uGeo.px.w / uGeo.px.h, 2.5, 7];
      var fracs = [-9, 0, 0.5, 9];
      var wFracs = [0.001, 0.2, 0.8, 9];
      var checked = 0, outside = 0, disagreed = 0;
      for (var ai = 0; ai < aspects.length; ai++) {
        for (var si = 0; si < fracs.length; si++) {
          for (var sj = 0; sj < fracs.length; sj++) {
            for (var wi = 0; wi < wFracs.length; wi++) {
              var uP = clampPlace(fracs[si] * uGeo.zoneWIn, fracs[sj] * uGeo.zoneHIn,
                wFracs[wi] * uGeo.zoneWIn, aspects[ai], uGeo, { snap: true });
              var uR = logoRectPx(uGeo, {
                wIn: uP.wIn, lxIn: uP.lxIn, lyIn: uP.lyIn, aspect: aspects[ai]
              });
              checked++;
              if (!uR || !uBox) { outside++; continue; }
              if (uR.x - uBox.x < -1e-6 || uR.y - uBox.y < -1e-6
                || (uR.x + uR.w) - (uBox.x + uBox.w) > 1e-6
                || (uR.y + uR.h) - (uBox.y + uBox.h) > 1e-6) outside++;

              // THE PARITY CLAIM, taken pixel for pixel. The same gesture on the
              // same drawn box must land the logo on the same screen rectangle
              // whether or not somebody typed a number into the mapper. If this
              // ever fails, the pixel space has stopped being a change of unit
              // and become a change of behaviour.
              var mP = clampPlace(fracs[si] * mGeo.zoneWIn, fracs[sj] * mGeo.zoneHIn,
                wFracs[wi] * mGeo.zoneWIn, aspects[ai], mGeo, { snap: true });
              var mR = logoRectPx(mGeo, {
                wIn: mP.wIn, lxIn: mP.lxIn, lyIn: mP.lyIn, aspect: aspects[ai]
              });
              if (!mR) { disagreed++; continue; }
              if (Math.abs(uR.x - mR.x) > 1e-6 || Math.abs(uR.y - mR.y) > 1e-6
                || Math.abs(uR.w - mR.w) > 1e-6 || Math.abs(uR.h - mR.h) > 1e-6) disagreed++;
              if (uP.snapped !== mP.snapped) disagreed++;
            }
          }
        }
      }
      ok(checked === aspects.length * fracs.length * fracs.length * wFracs.length,
        'the unmeasured sweep ran every combination');
      ok(outside === 0, 'an unmeasured logo never leaves its print area, in pixels');
      ok(disagreed === 0,
        'a measured and an unmeasured zone put the same gesture on the same pixels');

      // -- 3. THE GESTURE TUNABLES SURVIVE THE CHANGE OF SPACE -----------------
      //
      // Each of these is a PHYSICAL constant at the call site. Passed into pixel
      // space unconverted they do not fail loudly — they produce a quarter of a
      // pixel, which reads on screen as "the control is broken", which is the
      // failure this whole task exists to remove.
      //
      // THE MEASURE OF "SAME CONTROL" IS THE PIXEL, so each tunable is checked
      // by what it comes out as ON SCREEN in both geometries. The pair share a
      // drawn box, so equal on-screen distances is the strongest form the claim
      // can take — and the form a customer would notice failing.
      var uTol = snapTolFor(uGeo, { snapTolIn: DEFAULTS.snapTolIn });
      var mTol = snapTolFor(mGeo, { snapTolIn: DEFAULTS.snapTolIn });
      near(uTol * uGeo.pxPerIn, mTol * mGeo.pxPerIn, 1e-9,
        'the snap tolerance is the same on-screen distance in both spaces');
      ok(uTol * uGeo.pxPerIn > 1, 'and is a usable one, not a fraction of a pixel');
      ok(snapTolFor(mGeo, { snapTolIn: 0.4 }) === 0.4,
        'a measured zone still honours the caller\'s tolerance verbatim');
      ok(snapTolFor(mGeo, {}) === DEFAULTS.snapTolIn, 'and falls back to the physical default');
      ok(snapTolFor(null, {}) === DEFAULTS.snapTolIn, 'snapTolFor survives a null geometry');
      // A caller that widens the magnetism must widen it in BOTH spaces, or the
      // tunable silently stops being one on 676 of the store's 680 zones.
      ok(snapTolFor(uGeo, { snapTolIn: 0.5 }) === 2 * snapTolFor(uGeo, { snapTolIn: 0.25 }),
        'and an overridden tolerance still scales an unmeasured zone');

      ok(keyStepFor(mGeo, false) === DEFAULTS.keyStepIn, 'a measured arrow step is unchanged');
      ok(keyStepFor(mGeo, true) === DEFAULTS.keyStepShiftIn, 'as is the shifted one');
      near(keyStepFor(uGeo, false) * uGeo.pxPerIn, keyStepFor(mGeo, false) * mGeo.pxPerIn, 1e-9,
        'an unmeasured arrow press moves the same on-screen distance');
      ok(keyStepFor(uGeo, false) * uGeo.pxPerIn > 1, 'which is a visible one');
      ok(keyStepFor(uGeo, true) > keyStepFor(uGeo, false), 'and shift still moves further');
      ok(keyStepFor(null, false) === DEFAULTS.keyStepIn, 'keyStepFor survives a null geometry');

      // The restore button's completion mark. Unconverted, 0.06 would be six
      // percent of the whole print area and a clearly visible nudge would keep
      // the mark lit — the button would claim nothing had changed.
      near(stdTolFor(uGeo) * uGeo.pxPerIn, stdTolFor(mGeo) * mGeo.pxPerIn, 1e-9,
        'the standard-tolerance band is the same on-screen distance in both spaces');
      ok(stdTolFor(mGeo) === DEFAULTS.stdTolIn, 'and is the physical default when measured');
      ok(stdTolFor(uGeo) < DEFAULTS.stdTolIn * 0.5,
        'and is far tighter than six percent of an unmeasured zone');
      var uStdRec = standardPlacement(uGeo, 1.5, 'top', 'center');
      ok(isStandard(uStdRec, uStdRec, stdTolFor(uGeo)),
        'a re-derived unmeasured default still reports as standard');
      ok(!isStandard({ wIn: uStdRec.wIn + uGeo.zoneWIn * 0.05, lxIn: uStdRec.lxIn, lyIn: uStdRec.lyIn },
        uStdRec, stdTolFor(uGeo)),
        'and a five-percent nudge on an unmeasured zone clears the mark');

      // The snap must actually engage. A tolerance that is merely non-zero is
      // not the claim; the claim is that a customer who drags near the centre
      // gets pulled to it, exactly as they do on a measured product.
      var nearCentre = clampPlace((uGeo.zoneWIn - uGeo.zoneWIn * 0.5) / 2 + uTol * 0.5, 0,
        uGeo.zoneWIn * 0.5, 1, uGeo, { snap: true });
      ok(nearCentre.snapped === true, 'centre snap engages inside the tolerance on an unmeasured zone');
      var offCentre = clampPlace((uGeo.zoneWIn - uGeo.zoneWIn * 0.5) / 2 + uTol * 3, 0,
        uGeo.zoneWIn * 0.5, 1, uGeo, { snap: true });
      ok(offCentre.snapped === false, 'and lets go outside it');

      // -- 4. ONE RECORD, THREE SURFACES --------------------------------------
      //
      // THE ASSERTION THAT DICTATED THE CHOICE OF UNIT, and the reason it is the
      // zone rather than the rendered pixel.
      //
      // A placement record is written once and drawn on surfaces that measure
      // the SAME zone at different sizes: the desktop hero, the mobile corner
      // card (single-builder.js _stickyGeoContext — "same inch values, different
      // pixels-per-inch"), and the 2-step's nominal 600x600 bake probe. In raw
      // pixels a width of 80 would be a different fraction of the zone on each
      // of the three, so a logo sized on the hero would come out wrong on the
      // card and wrong again in the bake — silently, since every one of them
      // would still clamp cleanly inside its own boundary.
      //
      // Sweeping the same drawn box across three box sizes and requiring one
      // record to land on the same PROPORTION of each is what makes that
      // impossible rather than merely unlikely.
      var boxes = [[600, 600], [240, 240], [900, 720]];
      var sGeos = [];
      for (var bi = 0; bi < boxes.length; bi++) {
        sGeos.push(zoneGeometry(area, drawn, boxes[bi][0], boxes[bi][1]));
      }
      ok(sGeos[0] && sGeos[1] && sGeos[2], 'the same unmeasured zone resolves at three sizes');
      if (sGeos[0] && sGeos[1] && sGeos[2]) {
        ok(sGeos[0].zoneWIn === sGeos[1].zoneWIn && sGeos[1].zoneWIn === sGeos[2].zoneWIn,
          'the zone is the same width in every one of them, whatever the box');

        // One record, authored against the first surface, rendered on all three.
        var shared = clampPlace(0.2, 0.1, 0.55, 1.4, sGeos[0], { snap: false });
        var drift = 0;
        var base = null;
        for (var sgi = 0; sgi < sGeos.length; sgi++) {
          var sBox = clampRectPx(sGeos[sgi]);
          var sR = logoRectPx(sGeos[sgi], {
            wIn: shared.wIn, lxIn: shared.lxIn, lyIn: shared.lyIn, aspect: 1.4
          });
          if (!sBox || !sR) { drift++; continue; }
          // Expressed as fractions of each surface's own boundary, so three
          // different pixel figures are compared as the one thing that must
          // match: where the logo sits in its print area.
          var asFrac = {
            w: sR.w / sBox.w,
            x: (sR.x - sBox.x) / sBox.w,
            y: (sR.y - sBox.y) / sBox.h
          };
          if (!base) { base = asFrac; continue; }
          if (Math.abs(asFrac.w - base.w) > 1e-9
            || Math.abs(asFrac.x - base.x) > 1e-9
            || Math.abs(asFrac.y - base.y) > 1e-9) drift++;
        }
        ok(drift === 0,
          'one unmeasured record lands on the same proportion of the zone on every surface');

        // And the clamp agrees with itself across surfaces: a width the hero
        // permits must not be one the corner card rejects, or the logo would
        // jump when the customer switched between them.
        var clampDrift = 0;
        for (var ci = 0; ci < sGeos.length; ci++) {
          var c0 = clampPlace(9, 9, 9, 1.4, sGeos[ci], { snap: true });
          var c1 = clampPlace(9, 9, 9, 1.4, sGeos[0], { snap: true });
          if (Math.abs(c0.wIn - c1.wIn) > 1e-9 || Math.abs(c0.lxIn - c1.lxIn) > 1e-9
            || Math.abs(c0.lyIn - c1.lyIn) > 1e-9) clampDrift++;
        }
        ok(clampDrift === 0, 'and the clamp gives the same answer on every surface');
      }

      // -- 5. THE BAKE IS UNAFFECTED ------------------------------------------
      //
      // toCompositorRect is the payload the mockup compositor renders from, and
      // it consumes RATIOS: builder-shared-services.js:760 derives its own
      // pixels-per-unit as zoneWidthPx / zoneWIn and multiplies. So the same
      // placement must produce the same PICTURE in either space — the customer's
      // drag on an unmeasured zone bakes exactly where they put it, and it is
      // the label, not the render, that the missing measurement costs them.
      var uStd = standardPlacement(uGeo, 1.6, 'top', 'center');
      var mStd = standardPlacement(mGeo, 1.6, 'top', 'center');
      var uRect = toCompositorRect(uStd, uGeo);
      var mRect = toCompositorRect(mStd, mGeo);
      near(uRect.wIn / uRect.zoneWIn, mRect.wIn / mRect.zoneWIn, 1e-6,
        'the baked logo is the same fraction of the zone either way');
      near(uRect.lxIn / uRect.zoneWIn, mRect.lxIn / mRect.zoneWIn, 1e-6,
        'and sits at the same fraction across it');
      near(uRect.lyIn / uRect.zoneWIn, mRect.lyIn / mRect.zoneWIn, 1e-6,
        'and at the same fraction down it');

      // -- 6. NOT ONE PHYSICAL NUMBER ESCAPES ---------------------------------
      //
      // The half that must not go wrong. Everything above makes the control
      // work; this makes sure the working control never claims to know a size it
      // does not. Swept over the same placements the sweep above produced, so
      // "no inch string anywhere" is checked against real values rather than one
      // hand-picked record.
      var leaked = 0, unmarked = 0, recs = 0;
      for (var li = 0; li < aspects.length; li++) {
        for (var lw = 0; lw < wFracs.length; lw++) {
          var pl = clampPlace(0.3 * uGeo.zoneWIn, 0.2 * uGeo.zoneHIn,
            wFracs[lw] * uGeo.zoneWIn, aspects[li], uGeo, { snap: false });
          var rec = {
            wIn: pl.wIn, lxIn: pl.lxIn, lyIn: pl.lyIn,
            aspect: aspects[li], unit: 'po', measured: uGeo.measured
          };
          var f = cartMeasureFields(rec);
          recs++;
          if (!f) { leaked++; continue; }
          if (f.measured !== false) leaked++;
          // No digit, anywhere. A marker that carried "62" would be one regex
          // away from being read as a dimension by whoever writes the next
          // order-export script.
          if (/[0-9]/.test(f.size) || /[0-9]/.test(f.pos)) leaked++;
          if (/in|cm|po|"|″/.test(f.size.replace(UNMEASURED_MARK, ''))) leaked++;
          if (f.unit !== null || f.wIn !== null || f.hIn !== null) leaked++;
          if (f.size !== UNMEASURED_MARK || f.pos !== UNMEASURED_MARK) unmarked++;
          if (cartMeasureLabel(rec, 'po', '.', 'po') !== null) leaked++;
          if (cartMeasureLabel(rec, 'cm', ',', 'cm') !== null) leaked++;
        }
      }
      ok(recs === aspects.length * wFracs.length, 'the cart sweep ran every placement');
      ok(leaked === 0, 'no unmeasured placement produces a physical dimension anywhere');
      ok(unmarked === 0, 'and every one of them is explicitly marked instead');
    })();

    // -- the cart boundary, on its own -----------------------------------------
    //
    // cartMeasureFields is the last thing between a browser and a printing
    // press, so its polarity is asserted from both sides rather than assumed
    // from the sweep above.
    var cartMeasured = cartMeasureFields({
      wIn: 11.04, lxIn: 0.48, lyIn: 1.52, aspect: 2, unit: 'cm', measured: true
    });
    ok(cartMeasured && cartMeasured.measured === true, 'a measured record still reports measured');
    ok(cartMeasured && cartMeasured.size === '11.0x5.5 in', 'and emits true inches at one decimal');
    ok(cartMeasured && cartMeasured.pos === '0.5,1.5 in', 'and its position in the same unit');
    ok(cartMeasured && cartMeasured.unit === 'cm', 'and the unit the customer was reading');
    ok(cartMeasureLabel({ wIn: 11, lxIn: 0, lyIn: 0, aspect: 1, measured: true }, 'po', ',', 'po')
      === '11,0 × 11,0 po', 'the visible line is locale-formatted from the same record');

    // FAIL CLOSED. Every one of these is a record that is not provably measured,
    // and every one must be marked rather than sized. The absent flag is the
    // case that will actually happen: a blob saved by a build that predates the
    // flag, restored by one that expects it.
    var notProvable = [
      { wIn: 11, lxIn: 0, lyIn: 0, aspect: 1 },
      { wIn: 11, lxIn: 0, lyIn: 0, aspect: 1, measured: undefined },
      { wIn: 11, lxIn: 0, lyIn: 0, aspect: 1, measured: false },
      { wIn: 11, lxIn: 0, lyIn: 0, aspect: 1, measured: 'true' },
      { wIn: 11, lxIn: 0, lyIn: 0, aspect: 1, measured: 1 }
    ];
    var closedOk = true;
    for (var ni = 0; ni < notProvable.length; ni++) {
      var nf = cartMeasureFields(notProvable[ni]);
      if (!nf || nf.measured !== false || nf.size !== UNMEASURED_MARK
        || nf.pos !== UNMEASURED_MARK || nf.unit !== null) closedOk = false;
      if (cartMeasureLabel(notProvable[ni], 'po', '.', 'po') !== null) closedOk = false;
    }
    ok(closedOk, 'anything short of measured === true fails closed to the marker');
    // A truthy-but-wrong flag must not squeak through: `measured: 'false'` is a
    // non-empty string and would pass a `!!` test.
    ok(cartMeasureFields({ wIn: 11, aspect: 1, measured: 'false' }).measured === false,
      'the flag is compared by identity, never by truthiness');

    // The marker's own contract. Whoever writes the next order-export script
    // will reach for parseFloat and a "<w>x<h> in" regex; both must miss.
    ok(isNaN(parseFloat(UNMEASURED_MARK)), 'the marker is not a number to parseFloat');
    ok(!/(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)\s*in/.test(UNMEASURED_MARK),
      'and does not match the size pattern the measured branch emits');
    ok(UNMEASURED_MARK !== '', 'and is not empty, which would read as absent');

    // NOTHING AT ALL for a record with no usable width. This is the pre-existing
    // "the customer never sized this placement" case and it must stay distinct
    // from "sized, but the zone has no scale" — the first bakes at the historic
    // contain fit, the second bakes where the customer put it.
    ok(cartMeasureFields(null) === null, 'no record contributes nothing');
    ok(cartMeasureFields({ wIn: 0, measured: true }) === null, 'a zero width contributes nothing');
    ok(cartMeasureFields({ wIn: -4, measured: true }) === null, 'a negative width contributes nothing');
    ok(cartMeasureFields({ wIn: 'abc', measured: true }) === null, 'a non-numeric width contributes nothing');
    ok(cartMeasureFields({ wIn: Infinity, measured: true }) === null, 'a non-finite width contributes nothing');

    // -- rotateDelta ----------------------------------------------------------
    var r90 = rotateDelta(10, 0, 90);
    near(r90.dx, 0, 0.0001, 'rotateDelta 90deg dx');
    near(r90.dy, -10, 0.0001, 'rotateDelta 90deg dy');
    var r0 = rotateDelta(7, -3, 0);
    ok(r0.dx === 7 && r0.dy === -3, 'rotateDelta is identity at zero angle');
    var rBad = rotateDelta(7, -3, NaN);
    ok(rBad.dx === 7 && rBad.dy === -3, 'rotateDelta is identity on a non-finite angle');

    // -- isStandard -----------------------------------------------------------
    var std = { wIn: 11, lxIn: 0.5, lyIn: 1.5 };
    ok(isStandard({ wIn: 11.05, lxIn: 0.52, lyIn: 1.53 }, std), 'isStandard true inside tolerance');
    ok(!isStandard({ wIn: 11.2, lxIn: 0.5, lyIn: 1.5 }, std), 'isStandard false when width drifts');
    ok(!isStandard({ wIn: 11, lxIn: 0.7, lyIn: 1.5 }, std), 'isStandard false when x drifts');
    ok(!isStandard({ wIn: 11, lxIn: 0.5, lyIn: 1.8 }, std), 'isStandard false when y drifts');

    // -- the standard-tolerance BOUNDARY (plan 37-09) --------------------------
    // This predicate is the restore button's completion mark, so the boundary
    // itself has to be pinned: a customer who nudges a logo a sixteenth of an
    // inch must keep the mark, and one who nudges it further must lose it.
    // All three values at once — the right size in the wrong place is not
    // standard.
    //
    // Asserted just INSIDE the boundary, not exactly on it: 11 + 0.06 differs
    // from 11 by 0.06000000000000085 in IEEE 754, so an exact-boundary triple
    // fails on two of its three axes purely through representation error.
    // Nothing user-facing can depend on the boundary to the last bit, and
    // widening the comparison with an epsilon would only move the same fuzz
    // somewhere less visible.
    var stdT = DEFAULTS.stdTolIn;
    ok(isStandard({ wIn: 11 + stdT * 0.99, lxIn: 0.5 + stdT * 0.99, lyIn: 1.5 + stdT * 0.99 }, std),
      'isStandard true just inside the tolerance on all three values at once');
    ok(isStandard({ wIn: 11 - stdT * 0.99, lxIn: 0.5 - stdT * 0.99, lyIn: 1.5 - stdT * 0.99 }, std),
      'isStandard true just inside the negative tolerance boundary');
    ok(!isStandard({ wIn: 11 + stdT * 1.5, lxIn: 0.5, lyIn: 1.5 }, std),
      'isStandard false when width ALONE exceeds the tolerance');
    ok(!isStandard({ wIn: 11, lxIn: 0.5 + stdT * 1.5, lyIn: 1.5 }, std),
      'isStandard false when x ALONE exceeds the tolerance');
    ok(!isStandard({ wIn: 11, lxIn: 0.5, lyIn: 1.5 + stdT * 1.5 }, std),
      'isStandard false when y ALONE exceeds the tolerance');
    ok(isStandard({ wIn: 11.5, lxIn: 0.5, lyIn: 1.5 }, std, 1),
      'isStandard honours an overridden tolerance');
    ok(!isStandard(null, std), 'isStandard false with no placement');
    ok(!isStandard({ wIn: 11, lxIn: 0.5, lyIn: 1.5 }, null), 'isStandard false with no default');

    // Restore must equal the seed. Both call standardPlacement with identical
    // arguments, so the guarantee reduces to determinism — assert it rather
    // than assume it, because a stray Math.random or Date-based tiebreak in
    // here would show up as a logo that jumps when the button is pressed.
    if (geo) {
      var seedA = standardPlacement(geo, 2.381, 'top', 'center', { fitFrac: 0.9, centre: true });
      var seedB = standardPlacement(geo, 2.381, 'top', 'center', { fitFrac: 0.9, centre: true });
      ok(seedA.wIn === seedB.wIn && seedA.lxIn === seedB.lxIn && seedA.lyIn === seedB.lyIn,
        'standardPlacement is deterministic, so restore reproduces the seed exactly');
      ok(isStandard(seedA, seedB), 'a re-derived default reports as standard against itself');
    }

    // -- format / parse -------------------------------------------------------
    ok(formatMeasure(11, 'po', '.') === '11.0', 'formatMeasure inches');
    ok(formatMeasure(11, 'cm', ',') === '27,9', 'formatMeasure cm with a comma separator');
    near(parseMeasure('27,9', 'cm'), 10.98, 0.01, 'parseMeasure accepts a comma decimal');
    near(parseMeasure('27.9', 'cm'), 10.98, 0.01, 'parseMeasure accepts a period decimal');
    ok(parseMeasure('abc', 'po') === null, 'parseMeasure rejects text');
    ok(parseMeasure('-3', 'po') === null, 'parseMeasure rejects a negative');
    ok(parseMeasure('0', 'po') === null, 'parseMeasure rejects zero');
    ok(parseMeasure('', 'po') === null, 'parseMeasure rejects empty');
    ok(parseMeasure(null, 'po') === null, 'parseMeasure rejects null');
    ok(parseMeasure('1e999', 'po') === null, 'parseMeasure rejects a non-finite');
    ok(parseMeasure('11', 'po') === 11, 'parseMeasure passes inches through');

    // -- the readout's contract (plan 37-09) ----------------------------------
    // A comma and a period must be INDISTINGUISHABLE, not merely both accepted.
    // The FR route types commas and the EN route types periods into the same
    // field, and a customer pasting one into the other language must not get a
    // different logo.
    ok(parseMeasure('27,9', 'cm') === parseMeasure('27.9', 'cm'),
      'parseMeasure treats a comma and a period identically');
    ok(parseMeasure('11,5', 'po') === parseMeasure('11.5', 'po'),
      'parseMeasure treats the separators identically in inches too');
    ok(parseMeasure('  11.5  ', 'po') === 11.5, 'parseMeasure tolerates surrounding whitespace');
    near(parseMeasure('30', 'cm'), 30 / CM_PER_IN, 1e-9, 'parseMeasure converts centimetres to inches');
    ok(parseMeasure('11', 'PO') === 11, 'parseMeasure is case-insensitive about the unit');
    ok(parseMeasure('11', 'CM') === parseMeasure('11', 'cm'), 'parseMeasure lowercases cm too');
    ok(parseMeasure('  ', 'po') === null, 'parseMeasure rejects whitespace only');
    ok(parseMeasure('.', 'po') === null, 'parseMeasure rejects a bare separator');
    ok(parseMeasure(',', 'po') === null, 'parseMeasure rejects a bare comma');

    // Format into centimetres, type that number back in, get the same logo.
    // One decimal is all the readout shows, so a tenth of an inch is the
    // tightest the round trip can possibly be — assert exactly that bound.
    var rtWidths = [0.75, 2, 4.5, 7.25, 11, 12];
    var fmtRtOk = true;
    for (var fi = 0; fi < rtWidths.length; fi++) {
      var shownCm = formatMeasure(rtWidths[fi], 'cm', '.');
      var backCm = parseMeasure(shownCm, 'cm');
      var shownIn = formatMeasure(rtWidths[fi], 'po', ',');
      var backIn = parseMeasure(shownIn, 'po');
      if (backCm === null || Math.abs(backCm - rtWidths[fi]) > 0.1) fmtRtOk = false;
      if (backIn === null || Math.abs(backIn - rtWidths[fi]) > 0.1) fmtRtOk = false;
    }
    ok(fmtRtOk, 'format then parse round-trips within a tenth of an inch in both units');

    // The comma the FR route formats with must survive its own parse.
    ok(parseMeasure(formatMeasure(11, 'cm', ','), 'cm') !== null,
      'a comma-formatted value parses back');

    // -- toCompositorRect -----------------------------------------------------
    var rect = toCompositorRect({ lxIn: 1 / 3, lyIn: 2 / 3, wIn: 11.00004 }, { zoneWIn: 12 });
    var keys = [];
    for (var kk in rect) { if (Object.prototype.hasOwnProperty.call(rect, kk)) keys.push(kk); }
    keys.sort();
    ok(keys.join(',') === 'lxIn,lyIn,wIn,zoneWIn', 'toCompositorRect has exactly the four keys');
    ok(rect.lxIn === 0.3333, 'toCompositorRect rounds to four decimals');
    ok(rect.wIn === 11, 'toCompositorRect rounds float noise away');

    // -- paintedRect ----------------------------------------------------------
    var pr = paintedRect(1000, 500, 600, 600);
    near(pr.w, 600, 0.01, 'paintedRect width fills the constrained axis');
    near(pr.h, 300, 0.01, 'paintedRect height follows the aspect');
    near(pr.y, 150, 0.01, 'paintedRect centres the letterbox');
    var prBad = paintedRect(0, 500, 600, 600);
    ok(prBad.w === 600 && prBad.h === 600, 'paintedRect returns the full box on bad input');

    return { passed: passed, failed: failures.length, failures: failures };
  }

  var api = {
    CM_PER_IN: CM_PER_IN,
    DEFAULTS: DEFAULTS,
    toInches: toInches,
    fromInches: fromInches,
    zonePx: zonePx,
    paintedRect: paintedRect,
    zoneGeometry: zoneGeometry,
    minWidthIn: minWidthIn,
    REFERENCE_ZONE_IN: REFERENCE_ZONE_IN,
    inSpace: inSpace,
    snapTolFor: snapTolFor,
    keyStepFor: keyStepFor,
    stdTolFor: stdTolFor,
    fractionToInches: fractionToInches,
    inchesToFraction: inchesToFraction,
    standardPlacement: standardPlacement,
    clampRectPx: clampRectPx,
    logoRectPx: logoRectPx,
    clampPlace: clampPlace,
    clampWidth: clampWidth,
    rotateDelta: rotateDelta,
    isStandard: isStandard,
    formatMeasure: formatMeasure,
    parseMeasure: parseMeasure,
    toCompositorRect: toCompositorRect,
    UNMEASURED_MARK: UNMEASURED_MARK,
    cartMeasureFields: cartMeasureFields,
    cartMeasureLabel: cartMeasureLabel,
    __selfTest: __selfTest
  };

  if (typeof window !== 'undefined') window.forteeLogoPlacement = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
